import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  HomeIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  DocumentIcon,
  ArrowRightOnRectangleIcon,
  QuestionMarkCircleIcon,
  VideoCameraIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ChevronDownIcon,

  ChevronRightIcon,
  Cog6ToothIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  FolderIcon,
  FolderOpenIcon,
  PlusIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  BanknotesIcon,
  UserCircleIcon,
  EnvelopeIcon,
  SparklesIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';

import { getUserTimezone, formatDateTimeInTimezone } from '../services/timezoneUtils';
import type { ClientDashboardData, ClientAppointment, QuestionnaireResponsesData } from '../services/pracmagicTypes';
import ActionItemsPanel from './ActionItemsPanel';

import CheckInsTab from './CheckInsTab';
import SessionSummaryPanel from './SessionSummaryPanel';
import {
  listPacketsClient,
  DocumentPacketSummary,
  DocumentItem,
  ClientFolder,
  listClientOwnFiles,
  listClientOwnFolders,
  createClientFolderSvc,
  clientUploadDocument,
  deleteClientFile,
  getDocumentViewUrl,
  getPacketByToken,
  documentViewUrl,
  moveDocument,
  movePacketToFolder,
  ViewUrlResponse,
} from '../services/documentService';
import DocumentViewerPanel from './DocumentViewerPanel';
import ProfileCompletionBanner from './ProfileCompletionBanner';
import { useNavigate } from 'react-router-dom';
import PaymentModal from './PaymentModal';
import PackagePaymentModal from './PackagePaymentModal';
import AddressFields, { type ApiCountry } from './AddressFields';
import NotificationPrefsPanel from './NotificationPrefsPanel';
import type { EventDef as NotifEventDef } from './NotificationPrefsPanel';
import BookSessionModal from './BookSessionModal';
import { AppointmentPaymentStatus, formatCents, getCoachLinkAccounts, LinkAccount } from '../services/paymentService';
import { Invoice, listMyInvoices } from '../services/invoiceService';
import { buildSymbolMap, currencySymbol, FALLBACK_SYMBOL_MAP, SymbolMap } from '../utils/currency';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useHeaderUser } from '../contexts/HeaderContext';


// ─── Brand color utilities ────────────────────────────────────────────────────

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : null;
}
function getContrastColor(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255 > 0.5 ? '#1a1a1a' : '#ffffff';
}
function darkenHex(hex: string, pct: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = Math.max(0, Math.round(rgb.r * (1 - pct / 100)));
  const g = Math.max(0, Math.round(rgb.g * (1 - pct / 100)));
  const b = Math.max(0, Math.round(rgb.b * (1 - pct / 100)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
function lightenHex(hex: string, pct: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * pct / 100));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * pct / 100));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * pct / 100));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}


type Section = 'home' | 'sessions' | 'action-items' | 'forms' | 'check-ins' | 'documents' | 'notes' | 'payments' | 'settings' | 'invoices';

interface ActiveEnrollment {
  id: string;
  package_id: string;
  package_name: string;
  coach_client_id: number;
  sessions_total: number;
  sessions_used: number;
  status: string;
  session_duration_minutes: number;
  cadence: string;
  /** price_cents ÷ 100 for display; currency from enrollment; sessions_used and sessions_total are integer counts */
  price_cents: number;
  currency: string;
}

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

// ── Settings Section (top-level component to avoid conditional hook violation) ──

type ClientSettingsSubSection = 'profile' | 'notifications' | 'ai' | 'privacy';

interface DeliveryPref {
  preferred_channel: 'email' | 'sms';
  email_verified: boolean;
  phone_verified: boolean;
  phone_on_file: boolean;
}

function SettingsSection({ settingsSubSection, setSettingsSubSection, onTermsAccepted, onPrivacyAccepted }: {
  settingsSubSection: ClientSettingsSubSection | null;
  setSettingsSubSection: (s: ClientSettingsSubSection | null) => void;
  onTermsAccepted?: (v: boolean) => void;
  onPrivacyAccepted?: (v: boolean) => void;
}) {
  const [settingsTermsAccepted, setSettingsTermsAccepted] = useState<boolean | null>(null);
  const [settingsTermsSaving, setSettingsTermsSaving] = useState(false);
  const [settingsTermsError, setSettingsTermsError] = useState('');
  const [settingsPrivacyAccepted, setSettingsPrivacyAccepted] = useState<boolean | null>(null);
  const [settingsPrivacySaving, setSettingsPrivacySaving] = useState(false);
  const [settingsPrivacyError, setSettingsPrivacyError] = useState('');

  // ── Full profile form ──────────────────────────────────────────────────────
  const [displayName, setDisplayName] = useState('');
  const [profile, setProfile] = useState({
    phone: '', date_of_birth: '',
    street_address: '', city: '', state_province: '', postal_code: '', country: '',
    occupation: '', employer: '', industry: '',
    marital_status: '', dependents: '',
    emergency_contact_name: '', emergency_contact_phone: '',
    bio: '',
  });
  const [apiCountries, setApiCountries] = useState<ApiCountry[]>([]);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpSending, setPhoneOtpSending] = useState(false);
  const [phoneOtpVerifying, setPhoneOtpVerifying] = useState(false);
  const [phoneOtpError, setPhoneOtpError] = useState<string | null>(null);
  const [aiConsent, setAiConsent] = useState<boolean | null>(null);
  const [aiConsentAt, setAiConsentAt] = useState<string | null>(null);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState('');
  const [consentSuccess, setConsentSuccess] = useState('');
  const [showConsentInfoModal, setShowConsentInfoModal] = useState(false);
  const [consentInfoChoice, setConsentInfoChoice] = useState<boolean | null>(null);
  const [consentInfoSubmitting, setConsentInfoSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${config.apiBaseUrl}/countries`).then(r => r.ok ? r.json() : []).then(setApiCountries).catch(() => {});
    const token = localStorage.getItem('authToken') || '';
    Promise.all([
      fetch(`${config.apiBaseUrl}/countries`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${config.apiBaseUrl}${endpoints.main.clients.me}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([countries, d]) => {
      setApiCountries(countries);
      if (!d) return;
      setDisplayName(`${d.first_name || ''} ${d.last_name || ''}`.trim());
      setAiConsent(d.ai_consent ?? null);
      setAiConsentAt(d.ai_consent_at ?? null);
      setSettingsTermsAccepted(d.terms_accepted ?? null);
      setSettingsPrivacyAccepted(d.privacy_policy_accepted ?? null);
      setProfile({
        phone: d.phone || '',
        date_of_birth: d.date_of_birth ? d.date_of_birth.split('T')[0] : '',
        street_address: d.street_address || '',
        city: d.city || '',
        state_province: d.state_province || '',
        postal_code: d.postal_code || '',
        country: d.country || '',
        occupation: d.occupation || '',
        employer: d.employer || '',
        industry: d.industry || '',
        marital_status: d.marital_status || '',
        dependents: d.dependents != null ? String(d.dependents) : '',
        emergency_contact_name: d.emergency_contact_name || '',
        emergency_contact_phone: d.emergency_contact_phone || '',
        bio: d.bio || '',
      });
      setProfileLoaded(true);
    }).catch(() => setProfileLoaded(true));
  }, []);

  const handleSendPhoneOtp = async () => {
    setPhoneOtpSending(true);
    setPhoneOtpError(null);
    const token = localStorage.getItem('authToken') || '';
    try {
      const resp = await fetch(`${config.apiBaseUrl}/verify/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ channel: 'sms', phone: profile.phone }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed to send code');
      setPhoneOtpSent(true);
    } catch (e: any) {
      setPhoneOtpError(e.message);
    } finally {
      setPhoneOtpSending(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    setPhoneOtpVerifying(true);
    setPhoneOtpError(null);
    const token = localStorage.getItem('authToken') || '';
    try {
      const resp = await fetch(`${config.apiBaseUrl}/verify/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ channel: 'sms', code: phoneOtp }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Verification failed');
      setPref(p => p ? { ...p, phone_verified: true } : p);
      setPhoneOtpSent(false);
      setPhoneOtp('');
    } catch (e: any) {
      setPhoneOtpError(e.message);
    } finally {
      setPhoneOtpVerifying(false);
    }
  };

  const saveProfile = async () => {
    if (profile.phone && !isValidPhoneNumber(profile.phone)) {
      setPhoneError('Enter a valid phone number');
      return;
    }
    setPhoneError('');
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.clients.me}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(profile),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      setProfileSuccess('Profile saved.');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setProfileSaving(false);
    }
  };

  const updateConsent = async (decision: boolean) => {
    setConsentSaving(true);
    setConsentError('');
    setConsentSuccess('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.clients.me}/ai-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ consent: decision }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      setAiConsent(data.ai_consent);
      setAiConsentAt(data.ai_consent_at);
      setConsentSuccess('Preference saved.');
      setTimeout(() => setConsentSuccess(''), 3000);
    } catch (err) {
      setConsentError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setConsentSaving(false);
    }
  };

  const acceptLegal = async (field: 'terms_accepted' | 'privacy_policy_accepted') => {
    const isTerms = field === 'terms_accepted';
    if (isTerms) setSettingsTermsSaving(true); else setSettingsPrivacySaving(true);
    if (isTerms) setSettingsTermsError(''); else setSettingsPrivacyError('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.clients.me}/legal-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: true }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to save');
      if (isTerms) { setSettingsTermsAccepted(true); onTermsAccepted?.(true); }
      else { setSettingsPrivacyAccepted(true); onPrivacyAccepted?.(true); }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      if (isTerms) setSettingsTermsError(msg); else setSettingsPrivacyError(msg);
    } finally {
      if (isTerms) setSettingsTermsSaving(false); else setSettingsPrivacySaving(false);
    }
  };

  const field = (key: keyof typeof profile, label: string, type = 'text', placeholder = '') => (
    <div key={key}>
      <label className="block text-xs font-medium text-slate-400 mb-1">{label}</label>
      <input
        type={type}
        value={profile[key]}
        placeholder={placeholder}
        onChange={e => setProfile(p => ({ ...p, [key]: e.target.value }))}
        className="w-full text-sm bg-slate-700 border border-slate-600 text-gray-200 placeholder-slate-500 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProfile(p => ({ ...p, country: e.target.value, state_province: '' }));
  };

  // ── Delivery preference (pref.phone_verified used in profile form) ──────────
  const [pref, setPref] = useState<DeliveryPref | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken') || '';
    fetch(`${config.apiBaseUrl}${endpoints.main.clients.deliveryPref}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then((data: DeliveryPref | null) => { if (data) setPref(data); })
      .catch(() => {/* non-fatal */});
  }, []);

  // ── Sub-section breadcrumb ────────────────────────────────────────────────
  const subSectionLabel = settingsSubSection === 'profile' ? 'My Profile'
    : settingsSubSection === 'notifications' ? 'Notification Preferences'
    : settingsSubSection === 'ai' ? 'AI'
    : settingsSubSection === 'privacy' ? 'Privacy & Data' : '';
  const subNav = settingsSubSection ? (
    <div className="flex items-center gap-1.5 text-sm mb-6">
      <button onClick={() => setSettingsSubSection(null)} className="text-slate-400 hover:text-gray-100 transition-colors">
        Settings
      </button>
      <ChevronRightIcon className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
      <span className="text-gray-100 font-medium">{subSectionLabel}</span>
    </div>
  ) : null;

  // ── Profile sub-section ───────────────────────────────────────────────────
  if (settingsSubSection === 'profile') return (
    <div className="p-8 max-w-2xl space-y-6">
      {subNav}
      <h1 className="text-2xl font-bold text-gray-100">My Profile</h1>
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 space-y-5">
        {!profileLoaded ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="animate-pulse h-9 bg-slate-700 rounded-lg" />)}
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Basic Information</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-400 mb-1">Name</label>
                  <p className="text-sm text-gray-300 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg">{displayName || '—'}</p>
                  <p className="text-xs text-slate-500 mt-1">To update your name, contact your coach.</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                  <ReactPhoneInput
                    country="us"
                    value={profile.phone.replace(/^\+/, '')}
                    onChange={val => { setProfile(p => ({ ...p, phone: val ? `+${val}` : '' })); setPhoneError(''); setPhoneOtpSent(false); setPhoneOtp(''); setPhoneOtpError(null); }}
                    inputStyle={{ width: '100%', border: `1px solid ${phoneError ? '#ef4444' : '#d1d5db'}`, borderRadius: '0.5rem', fontSize: '0.875rem', height: '38px', color: '#111827' }}
                    buttonStyle={{ border: `1px solid ${phoneError ? '#ef4444' : '#d1d5db'}`, borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem', background: '#f9fafb' }}
                    enableSearch
                  />
                  {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
                  {profile.phone && !phoneError && (
                    <div className="mt-2">
                      {pref?.phone_verified ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Phone verified ✓</span>
                      ) : !phoneOtpSent ? (
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={handleSendPhoneOtp} disabled={phoneOtpSending} className="text-xs text-blue-600 hover:text-blue-500 disabled:opacity-50 transition-colors">
                            {phoneOtpSending ? 'Sending…' : 'Send verification code'}
                          </button>
                          {phoneOtpError && <p className="text-xs text-red-500">{phoneOtpError}</p>}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <input type="text" inputMode="numeric" maxLength={6} value={phoneOtp} onChange={e => setPhoneOtp(e.target.value.replace(/\D/g, ''))} placeholder="6-digit code" className="w-28 px-2 py-1 text-xs border border-slate-300 rounded-lg text-gray-700 placeholder-slate-400 focus:outline-none focus:border-blue-500 tracking-widest text-center" />
                          <button type="button" onClick={handleVerifyPhoneOtp} disabled={phoneOtpVerifying || phoneOtp.length !== 6} className="text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg px-3 py-1 transition-colors">{phoneOtpVerifying ? 'Verifying…' : 'Verify'}</button>
                          <button type="button" onClick={() => { setPhoneOtpSent(false); setPhoneOtp(''); setPhoneOtpError(null); }} className="text-xs text-slate-500 hover:text-slate-700 transition-colors">Resend</button>
                          {phoneOtpError && <p className="w-full text-xs text-red-500 mt-0.5">{phoneOtpError}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {field('date_of_birth', 'Date of Birth', 'date')}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Address</p>
              <AddressFields
                country={profile.country} state_province={profile.state_province} city={profile.city}
                postal_code={profile.postal_code} street_address={profile.street_address} errors={{}}
                onChange={e => setProfile(p => ({ ...p, [e.target.name]: e.target.value }))}
                onCountryChange={handleCountryChange}
                countryOptions={apiCountries.length > 0 ? apiCountries : undefined}
                inputClassName="w-full text-sm bg-slate-700 border border-slate-600 text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                labelClassName="block text-xs font-medium text-slate-400 mb-1"
              />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Personal</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Marital Status</label>
                  <select value={profile.marital_status} onChange={e => setProfile(p => ({ ...p, marital_status: e.target.value }))} className="w-full text-sm bg-slate-700 border border-slate-600 text-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="">Select…</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="domestic_partnership">Domestic Partnership</option>
                    <option value="separated">Separated</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                </div>
                {field('dependents', 'Number of Dependents', 'number')}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Work</p>
              <div className="grid grid-cols-2 gap-4">
                {field('occupation', 'Occupation', 'text', 'e.g. Software Engineer')}
                {field('employer', 'Employer', 'text', 'Company name')}
                {field('industry', 'Industry', 'text', 'e.g. Technology')}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Emergency Contact</p>
              <div className="grid grid-cols-2 gap-4">
                {field('emergency_contact_name', 'Name')}
                {field('emergency_contact_phone', 'Phone', 'tel')}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Bio / Notes</label>
              <textarea rows={3} value={profile.bio} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))} placeholder="Anything you'd like your coach to know about you…" className="w-full text-sm bg-slate-700 border border-slate-600 text-gray-200 placeholder-slate-500 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" />
            </div>
            {profileError && <p className="text-sm text-red-400">{profileError}</p>}
            {profileSuccess && <p className="text-sm text-green-400 flex items-center gap-1.5"><CheckCircleIcon className="w-4 h-4" />{profileSuccess}</p>}
            <button onClick={saveProfile} disabled={profileSaving} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              {profileSaving ? 'Saving…' : 'Save Profile'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ── Notifications sub-section ─────────────────────────────────────────────
  if (settingsSubSection === 'notifications') return (
    <div className="p-8 max-w-2xl space-y-6">
      {subNav}
      <ClientNotificationPreferencesCard />
    </div>
  );

  // ── AI sub-section ────────────────────────────────────────────────────────
  if (settingsSubSection === 'ai') return (
    <div className="p-8 max-w-2xl space-y-6">
      {subNav}
      <h1 className="text-2xl font-bold text-gray-100">AI</h1>
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-200">AI Session Processing</h2>
            <p className="text-sm text-slate-400 mt-0.5">Your consent for AI-assisted note-taking and session summaries.</p>
          </div>
          <button
            onClick={() => setShowConsentInfoModal(true)}
            className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2 flex-shrink-0 mt-0.5 transition-colors"
          >
            What does this mean?
          </button>
        </div>
        <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${aiConsent === true ? 'bg-green-900/20 border-green-700/30' : aiConsent === false ? 'bg-red-900/20 border-red-700/30' : 'bg-amber-900/20 border-amber-700/30'}`}>
          <span className={`text-xl mt-0.5 flex-shrink-0 ${aiConsent === true ? 'text-green-400' : aiConsent === false ? 'text-red-400' : 'text-amber-400'}`}>
            {aiConsent === true ? '✓' : aiConsent === false ? '✗' : '?'}
          </span>
          <div>
            <p className={`text-sm font-semibold ${aiConsent === true ? 'text-green-300' : aiConsent === false ? 'text-red-300' : 'text-amber-300'}`}>
              {aiConsent === true ? 'You have consented to AI session processing' : aiConsent === false ? 'You have opted out of AI session processing' : "You haven't made a decision yet"}
            </p>
            {aiConsentAt && (
              <p className="text-xs text-slate-500 mt-0.5">
                {aiConsent === true ? 'Consented' : 'Updated'} on {new Date(aiConsentAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => updateConsent(true)} disabled={consentSaving || aiConsent === true} className="px-4 py-2 text-sm font-medium rounded-lg border border-green-700/50 text-green-400 hover:bg-green-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">I consent</button>
          <button onClick={() => updateConsent(false)} disabled={consentSaving || aiConsent === false} className="px-4 py-2 text-sm font-medium rounded-lg border border-red-700/50 text-red-400 hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Opt out</button>
        </div>
        {consentError && <p className="text-sm text-red-400">{consentError}</p>}
        {consentSuccess && <p className="text-sm text-green-400">{consentSuccess}</p>}
      </div>

      {showConsentInfoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
            <div>
              <h2 className="text-lg font-bold text-gray-100">AI-powered coaching — your choice</h2>
              <p className="text-sm text-slate-400 mt-1">This takes 60 seconds to read and is worth it.</p>
            </div>
            <div className="bg-blue-900/30 border border-blue-800/30 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-blue-300 uppercase tracking-wide">What AI enables for you</p>
              <ul className="space-y-1.5 text-sm text-blue-200">
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Session summaries</strong> — structured notes from every session, available to you and your coach.</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Action items</strong> — automatically captured and tracked between sessions so nothing falls through.</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Progress tracking</strong> — check-ins and trends that show how far you've come.</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Smart nudges</strong> — timely reminders on your action items so momentum doesn't stall.</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Prepared coach</strong> — your coach gets an AI briefing before each session so you spend less time catching up.</span></li>
              </ul>
            </div>
            <div className="space-y-3 text-sm text-slate-300">
              <p className="font-medium text-gray-200">How it works</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span>Sessions are recorded via <strong>Recall.ai</strong> and transcribed automatically. Transcripts are permanently deleted after 30 days.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span><strong>Your name is never sent to AI.</strong> Before any transcript reaches Claude, your name is replaced with <code className="bg-slate-700 px-1 rounded text-xs">[CLIENT]</code> — Anthropic never sees who you are.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span><strong>Anthropic does not train on your data.</strong> Session content is never used to train AI models. <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Read Anthropic's privacy policy →</a></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span>Session summaries are visible only to you and your coach. Your data is never sold or used for advertising.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span>You can change this decision at any time from your dashboard.</span>
                </li>
              </ul>
            </div>
            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${consentInfoChoice === true ? 'border-blue-500 bg-blue-900/30' : 'border-slate-600 hover:border-slate-500'}`}>
                <input type="radio" name="consentInfo" checked={consentInfoChoice === true} onChange={() => setConsentInfoChoice(true)} className="mt-0.5 accent-blue-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-100">I consent — enable AI features</p>
                  <p className="text-xs text-slate-400 mt-0.5">Session recording, AI summarization, action items, nudges, and progress tracking.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${consentInfoChoice === false ? 'border-slate-500 bg-slate-700' : 'border-slate-600 hover:border-slate-500'}`}>
                <input type="radio" name="consentInfo" checked={consentInfoChoice === false} onChange={() => setConsentInfoChoice(false)} className="mt-0.5 accent-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-300">I do not consent — manual only</p>
                  <p className="text-xs text-slate-400 mt-0.5">No recording or AI processing. Your coach will take manual notes.</p>
                </div>
              </label>
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={async () => {
                  if (consentInfoChoice === null) return;
                  setConsentInfoSubmitting(true);
                  try {
                    await updateConsent(consentInfoChoice);
                    setShowConsentInfoModal(false);
                  } finally {
                    setConsentInfoSubmitting(false);
                  }
                }}
                disabled={consentInfoSubmitting || consentInfoChoice === null}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl transition-colors"
              >
                {consentInfoSubmitting ? 'Saving…' : 'Save my choice'}
              </button>
              <button
                onClick={() => setShowConsentInfoModal(false)}
                className="px-4 py-2.5 border border-slate-600 text-slate-300 hover:bg-slate-700 text-sm font-medium rounded-xl transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Use */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-200">Terms of Use</h2>
          <p className="text-sm text-slate-400 mt-0.5">Review and accept the CoachSight Terms of Use.</p>
        </div>
        {settingsTermsAccepted ? (
          <p className="text-sm text-green-400">You have accepted the Terms of Use.</p>
        ) : (
          <>
            <p className="text-sm text-slate-400">
              Please read and accept our{' '}
              <a href="https://www.coachsightecr.com/terms" target="_blank" rel="noreferrer" className="text-violet-400 hover:underline">Terms of Use</a>
              {' '}to continue using CoachSight.
            </p>
            {settingsTermsError && <p className="text-sm text-red-400">{settingsTermsError}</p>}
            <button
              onClick={() => acceptLegal('terms_accepted')}
              disabled={settingsTermsSaving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {settingsTermsSaving ? 'Saving...' : 'Accept Terms of Use'}
            </button>
          </>
        )}
      </div>
    </div>
  );

  // ── Privacy sub-section ───────────────────────────────────────────────────
  if (settingsSubSection === 'privacy') return (
    <div className="p-8 max-w-2xl space-y-6">
      {subNav}
      <PrivacyCard
        privacyAccepted={settingsPrivacyAccepted}
        privacySaving={settingsPrivacySaving}
        privacyError={settingsPrivacyError}
        onAcceptPrivacy={() => acceptLegal('privacy_policy_accepted')}
      />
    </div>
  );

  // ── Landing page ──────────────────────────────────────────────────────────
  return (
    <div className="p-8 max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold text-gray-100">Settings</h1>
      <div className="grid grid-cols-2 gap-3">
        {([
          { key: 'profile'       as const, label: 'My Profile',              Icon: UserCircleIcon,  desc: 'Name, contact, address & emergency info' },
          { key: 'notifications' as const, label: 'Notification Preferences', Icon: EnvelopeIcon,    desc: 'Delivery channel, nudges, and per-event settings' },
          { key: 'ai'            as const, label: 'AI',                       Icon: SparklesIcon,    desc: 'Your consent for AI-assisted session processing' },
          { key: 'privacy'       as const, label: 'Privacy & Data',           Icon: ShieldCheckIcon, desc: 'Download your personal data' },
        ]).map(({ key, label, Icon, desc }) => (
          <button
            key={key}
            onClick={() => setSettingsSubSection(key)}
            className="bg-slate-800 border border-slate-700 rounded-xl p-4 text-left hover:border-blue-500/50 hover:bg-slate-700/50 transition-colors group"
          >
            <Icon className="w-5 h-5 text-slate-500 group-hover:text-blue-400 mb-2 transition-colors" />
            <p className="text-sm font-medium text-gray-100">{label}</p>
            <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Client Notification Preferences card ─────────────────────────────────────
function ClientNotificationPreferencesCard() {
  type NotifPref = { event_type: string; channel: string; email_override: string | null };
  const CLIENT_EVENTS: NotifEventDef[] = [
    { key: 'session_reminder',      label: 'Session Reminders',     desc: 'Reminders before each upcoming coaching session' },
    { key: 'session_notes_shared',  label: 'Notes / Action Items',  desc: 'Your coach shares session notes or action items with you' },
    { key: 'nudge',                 label: 'Nudges',                desc: 'Coaching reminders your coach sends to keep you on track' },
    { key: 'form_assigned',         label: 'Form assigned',         desc: 'Your coach assigns a form or questionnaire' },
    { key: 'form_due_reminder',     label: 'Form due reminder',     desc: 'A reminder that a form is due soon' },
    { key: 'invoice_sent',          label: 'Invoice sent',          desc: 'Your coach sends you an invoice' },
    { key: 'payment_confirmation',  label: 'Payment confirmation',  desc: 'A payment you made has been processed successfully' },
    { key: 'payment_failure',       label: 'Payment failure',       desc: 'A payment attempt was unsuccessful' },
  ];

  const [prefs, setPrefs] = useState<NotifPref[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [clientPhone, setClientPhone] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken') || '';
    setLoading(true);
    Promise.all([
      fetch(`${config.apiBaseUrl}/clients/me/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : []),
      fetch(`${config.apiBaseUrl}/clients/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : null),
    ]).then(([prefData, profile]) => {
      setPrefs(Array.isArray(prefData) ? prefData : []);
      if (profile) {
        setClientPhone(profile.phone || null);
        setPhoneVerified(!!profile.is_phone_verified);
      }
    }).finally(() => setLoading(false));
  }, []);

  const getPref = (key: string): NotifPref =>
    prefs.find(p => p.event_type === key) ?? { event_type: key, channel: 'email', email_override: null };

  const updatePref = (key: string, field: 'channel' | 'email_override', value: string) => {
    setPrefs(prev => {
      const existing = prev.find(p => p.event_type === key);
      if (existing) {
        return prev.map(p => p.event_type === key ? { ...p, [field]: value || null } : p);
      }
      return [...prev, { event_type: key, channel: 'email', email_override: null, [field]: value || null }];
    });
  };

  const smsIneligible = !phoneVerified || !clientPhone || !clientPhone.startsWith('+1');

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      const token = localStorage.getItem('authToken') || '';
      const payload = CLIENT_EVENTS.map(ev => {
        const p = getPref(ev.key);
        return { event_type: ev.key, channel: p.channel, email_override: p.email_override || null };
      });
      const res = await fetch(`${config.apiBaseUrl}/clients/me/notification-preferences`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveError(err.detail || 'Save failed. Please try again.');
        return;
      }
      const updated = await res.json();
      setPrefs(updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
      <div className="animate-pulse h-16 bg-slate-700 rounded-xl" />
    </div>
  );

  return (
    <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-200">Per-Event Preferences</h2>
        <p className="text-sm text-slate-400 mt-0.5">Choose how you receive each type of notification.</p>
      </div>
      <NotificationPrefsPanel
        events={CLIENT_EVENTS}
        prefs={prefs}
        onChange={updatePref}
        smsIneligible={smsIneligible}
        onSave={handleSave}
        saving={saving}
        saved={saveSuccess}
        error={saveError}
        showSmsDisclosure
      />
    </div>
  );
}

function PrivacyCard({ privacyAccepted, privacySaving, privacyError, onAcceptPrivacy }: {
  privacyAccepted?: boolean | null;
  privacySaving?: boolean;
  privacyError?: string;
  onAcceptPrivacy?: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/gdpr/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'coachsight-export.json';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Privacy Policy acceptance */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-200">Privacy Policy</h2>
          <p className="text-sm text-slate-400 mt-0.5">Review and accept the CoachSight Privacy Policy.</p>
        </div>
        {privacyAccepted ? (
          <p className="text-sm text-green-400">You have accepted the Privacy Policy.</p>
        ) : (
          <>
            <p className="text-sm text-slate-400">
              Please read and accept our{' '}
              <a href="https://www.coachsightecr.com/privacy" target="_blank" rel="noreferrer" className="text-violet-400 hover:underline">Privacy Policy</a>
              {' '}to continue using CoachSight.
            </p>
            {privacyError && <p className="text-sm text-red-400">{privacyError}</p>}
            <button
              onClick={onAcceptPrivacy}
              disabled={privacySaving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {privacySaving ? 'Saving...' : 'Accept Privacy Policy'}
            </button>
          </>
        )}
      </div>

      {/* Data download */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-200">Privacy & Data</h2>
          <p className="text-sm text-slate-400 mt-0.5">Download a copy of all personal data CoachSight ECR holds about you.</p>
        </div>
        <p className="text-sm text-slate-400">
          Your export includes your profile, appointments, session notes, action items, and questionnaire responses as a JSON file.
        </p>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {downloading ? 'Preparing download…' : 'Download My Data'}
        </button>
      </div>
    </div>
  );
}

// ── ClientChangePasswordForm ────────────────────────────────────────────────

function ClientChangePasswordForm({ changePassword }: { changePassword: (c: string, n: string, cf: string) => Promise<string> }) {
  const [form, setForm] = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);
    const err = await changePassword(form.current, form.newPw, form.confirm);
    if (err) setError(err);
    else { setSuccess('Password changed successfully'); setForm({ current: '', newPw: '', confirm: '' }); }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {success && <p className="text-xs text-green-400">{success}</p>}
      {error   && <p className="text-xs text-red-400">{error}</p>}
      {[
        { key: 'current', label: 'Current Password' },
        { key: 'newPw',   label: 'New Password' },
        { key: 'confirm', label: 'Confirm New Password' },
      ].map(({ key, label }) => (
        <div key={key}>
          <label className="block text-xs font-medium text-slate-400 mb-1">{label}</label>
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              value={(form as any)[key]}
              onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              className="w-full text-sm bg-slate-700 border border-slate-600 text-gray-200 rounded-lg px-2.5 py-2 pr-9 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button type="button" onClick={() => setShowPw(p => !p)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
              {showPw ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
          </div>
        </div>
      ))}
      <button
        type="submit"
        disabled={loading}
        className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {loading ? 'Saving…' : 'Update Password'}
      </button>
    </form>
  );
}

// ── Response Viewer Modal ─────────────────────────────────────────────────────

const ResponseViewerModal: React.FC<{
  title: string;
  questions: any[];
  onClose: () => void;
}> = ({ title, questions, onClose }) => {
  const typeLabels: Record<string, string> = {
    rating: 'Rating', yesno: 'Yes / No', freetext: 'Text',
    number: 'Number', dropdown_single: 'Choice', dropdown_multi: 'Multi-select',
  };

  function formatAnswer(q: any) {
    const pts = q.data_points ?? [];
    if (pts.length === 0) return <span className="text-slate-500 italic">No answer</span>;
    const latest = pts[pts.length - 1];
    if (q.type === 'rating' || q.type === 'number') {
      return <span className="text-blue-400 font-semibold">{latest.rating_value ?? '—'}</span>;
    }
    if (q.type === 'yesno') {
      return <span className={latest.bool_value ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>
        {latest.bool_value ? 'Yes' : 'No'}
      </span>;
    }
    if (q.type === 'dropdown_multi' && latest.text_value) {
      return (
        <div className="flex flex-wrap gap-1 mt-1">
          {latest.text_value.split('|||').map((v: string, i: number) => (
            <span key={i} className="text-xs bg-slate-700 border border-slate-600 rounded-full px-2 py-0.5 text-slate-300">{v}</span>
          ))}
        </div>
      );
    }
    return <span className="text-slate-200">{latest.text_value || '—'}</span>;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={onClose}>
      <div
        className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 flex-shrink-0">
          <h3 className="text-base font-semibold text-gray-100 truncate">{title}</h3>
          <button onClick={onClose} className="ml-3 flex-shrink-0 text-slate-500 hover:text-slate-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {questions.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-6">No responses recorded.</p>
          )}
          {questions.map((q: any, idx: number) => (
            <div key={q.id} className="bg-slate-900 border border-slate-700 rounded-xl p-4">
              <div className="flex items-start gap-2 mb-2">
                <span className="text-xs text-slate-500 mt-0.5 w-5 flex-shrink-0">{idx + 1}.</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-200">{q.text}</p>
                  <span className="text-xs text-slate-500">{typeLabels[q.type] ?? q.type}</span>
                </div>
              </div>
              <div className="pl-7 text-sm">
                {formatAnswer(q)}
              </div>
              {(q.data_points?.length ?? 0) > 1 && (
                <p className="pl-7 mt-1.5 text-xs text-slate-600">{q.data_points.length} submissions total</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────

const ClientDashboard: React.FC = () => {

  const [activeSection, setActiveSection] = useState<Section>(() => {
    const hash = window.location.hash.replace('#', '') as Section;
    const valid: Section[] = ['home', 'sessions', 'action-items', 'forms', 'check-ins', 'documents', 'notes', 'payments', 'settings', 'invoices'];
    return valid.includes(hash) ? hash : 'home';
  });
const [dashboard, setDashboard] = useState<ClientDashboardData | null>(null);
  const [appointments, setAppointments] = useState<ClientAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<ClientAppointment | null>(null);
  const [packets, setPackets] = useState<DocumentPacketSummary[]>([]);
  const [clientInvoices, setClientInvoices] = useState<Invoice[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [paymentStatuses, setPaymentStatuses] = useState<Record<string, AppointmentPaymentStatus>>({});
  const [payingAppointment, setPayingAppointment] = useState<ClientAppointment | null>(null);
  const [payingPackagePayment, setPayingPackagePayment] = useState<{ id: string; package_name: string; label: string } | null>(null);
  const [pendingCheckInsCount, setPendingCheckInsCount] = useState(0);
  const [pendingFormsCount, setPendingFormsCount] = useState(0);
  const [sessionsOpen, setSessionsOpen] = useState<Record<string, boolean>>({});
  const [sessionGroupOpen, setSessionGroupOpen] = useState<Record<string, boolean>>({});
  const [coachLinkAccounts, setCoachLinkAccounts] = useState<LinkAccount[]>([]);
  const [activeEnrollments, setActiveEnrollments] = useState<ActiveEnrollment[]>([]);
  const [pendingPackagePayments, setPendingPackagePayments] = useState<{
    id: string;
    enrollment_id: string;
    payment_type: string;
    installment_number: number | null;
    amount_cents: number;
    currency: string;
    status: string;
    due_date: string | null;
    invoiced_at: string | null;
    package_name: string;
    coach_email: string | null;
    coach_name: string | null;
    payment_url: string | null;
  }[]>([]);
  const [clientVerifyStatus, setClientVerifyStatus] = useState<{
    is_email_verified: boolean;
    is_phone_verified: boolean;
    has_phone: boolean;
  } | null>(null);
  const [aiConsent, setAiConsent] = useState<boolean | null | undefined>(undefined); // undefined = not loaded yet
  const [termsAccepted, setTermsAccepted] = useState<boolean | null>(null);
  const [privacyAccepted, setPrivacyAccepted] = useState<boolean | null>(null);
  const [setupCardOpen, setSetupCardOpen] = useState(true);
  const [showConsentModal, setShowConsentModal] = useState(false);
  // Client files (My Files section)
  const [clientFiles, setClientFiles] = useState<DocumentItem[]>([]);
  const [clientFolders, setClientFolders] = useState<ClientFolder[]>([]);
  const [docViewer, setDocViewer] = useState<ViewUrlResponse | null>(null);
  const [docViewLoading, setDocViewLoading] = useState<string | null>(null);
  const [packetViewer, setPacketViewer] = useState<{ title: string; docs: Array<{ url: string; fileType: string; docName: string }> } | null>(null);
  const [packetViewLoading, setPacketViewLoading] = useState<string | null>(null);
  const [responseViewer, setResponseViewer] = useState<{ title: string; questions: any[] } | null>(null);
  // Client file upload state
  const clientFileInputRef = useRef<HTMLInputElement>(null);
  const [clientUploadName, setClientUploadName] = useState('');
  const [clientUploadFile, setClientUploadFile] = useState<File | null>(null);
  const [clientUploadFolderId, setClientUploadFolderId] = useState('');
  const [clientUploading, setClientUploading] = useState(false);
  const [clientUploadError, setClientUploadError] = useState('');
  // Client folder navigation + creation
  const [selectedClientFolderId, setSelectedClientFolderId] = useState<string | null | 'all'>('all');
  const [newClientFolderName, setNewClientFolderName] = useState('');
  const [showClientFolderInput, setShowClientFolderInput] = useState(false);
  const [creatingClientFolder, setCreatingClientFolder] = useState(false);
  const [consentChoice, setConsentChoice] = useState<boolean | null>(null);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [showBookModal, setShowBookModal] = useState(false);

  // Past sessions for Sessions tab — fetched on demand with date range
  const _thirtyDaysAgo = new Date();
  _thirtyDaysAgo.setDate(_thirtyDaysAgo.getDate() - 30);
  const [pastFrom, setPastFrom] = useState<string>(_thirtyDaysAgo.toISOString().slice(0, 10));
  const [pastTo,   setPastTo]   = useState<string>(new Date().toISOString().slice(0, 10));
  const [pastSessions,  setPastSessions]  = useState<ClientAppointment[]>([]);
  const [pastLoading,   setPastLoading]   = useState(false);

  // Shared coach filter — persists as user navigates between sections
  const [coachFilter, setCoachFilter] = React.useState<string | null>(null);

  // Settings sub-section routing (hoisted so the name-click menu can navigate into sub-sections)
  const [settingsSubSection, setSettingsSubSection] = useState<ClientSettingsSubSection | null>(null);

  const [financialsOpen, setFinancialsOpen] = useState(() =>
    ['payments', 'invoices'].includes(window.location.hash.replace('#', ''))
  );

  // Profile menu (bottom-left name click dropdown)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profileMenuTab, setProfileMenuTab] = useState<'menu' | 'password'>('menu');
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  // Sync active section to URL hash so page refresh restores the same view
  useEffect(() => {
    window.location.hash = activeSection;
  }, [activeSection]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '') as Section;
      const valid: Section[] = ['home', 'sessions', 'action-items', 'forms', 'check-ins', 'documents', 'notes', 'payments', 'settings', 'invoices'];
      if (valid.includes(hash)) setActiveSection(hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const userData = (() => {
    try { return JSON.parse(localStorage.getItem('userData') || '{}'); } catch { return {}; }
  })();

  const clientId: string = userData.id || '';
  const firstName: string = userData.firstName || userData.first_name || 'there';
  const clientLastName: string = userData.lastName || userData.last_name || '';
  const clientEmail: string = userData.email || '';
  const clientInitials = [firstName, clientLastName].filter(Boolean).map(n => n[0].toUpperCase()).join('') || 'C';
  const timezone = getUserTimezone();

  const { setHeaderUser } = useHeaderUser();
  useEffect(() => {
    setHeaderUser([firstName, clientLastName].filter(Boolean).join(' ') || 'Client', clientInitials);
    return () => setHeaderUser(null, null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, clientLastName, clientInitials]);

  const handleLogout = () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetch(`${config.apiBaseUrl}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('userData');
    navigate('/', { replace: true });
  };

  // Close profile menu on outside click
  useEffect(() => {
    if (!profileMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileMenuOpen]);

  const changePassword = async (current: string, newPw: string, confirm: string): Promise<string> => {
    if (newPw !== confirm) return 'Passwords do not match';
    if (newPw.length < 12) return 'Password must be at least 12 characters';
    if (!/[A-Z]/.test(newPw)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(newPw)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(newPw)) return 'Password must contain at least one number';
    if (!/[^A-Za-z0-9]/.test(newPw)) return 'Password must contain at least one special character';
    const token = localStorage.getItem('authToken') || '';
    const res = await fetch(`${config.apiBaseUrl}/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ current_password: current, new_password: newPw }),
    });
    const data = await res.json();
    if (!res.ok) return data.detail || 'Failed to change password';
    return '';
  };

  const fetchData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken') || '';
      const headers = { Authorization: `Bearer ${token}` };

      const sessionsUrl = `${config.apiBaseUrl}${endpoints.main.clients.sessions(clientId)}?include_payments=true`;

      const [dashRes, apptRes] = await Promise.all([
        fetch(`${config.apiBaseUrl}${endpoints.main.clients.dashboard(clientId)}`, { headers }),
        fetch(sessionsUrl, { headers }),
      ]);

      if (dashRes.ok) {
        const dashData: ClientDashboardData = await dashRes.json();
        setDashboard(dashData);
        if (dashData.coach?.id) {
          getCoachLinkAccounts(dashData.coach.id).then(setCoachLinkAccounts).catch(() => {});
        }
      }

      if (apptRes.ok) {
        const apptData = await apptRes.json();
        const appts: ClientAppointment[] = apptData.appointments || [];
        setAppointments(appts);
        const statuses: Record<string, AppointmentPaymentStatus> = {};
        appts.forEach((a: any) => {
          if (a.id && a.payment_status) {
            statuses[a.id] = { appointment_id: a.id, ...a.payment_status };
          }
        });
        setPaymentStatuses(statuses);
      }
      setAppointmentsLoading(false);

      if (!dashRes.ok && !apptRes.ok) setError('Failed to load dashboard data');

      try {
        const p = await listPacketsClient();
        setPackets(p);
      } catch { /* non-fatal */ }

      try {
        const invs = await listMyInvoices();
        setClientInvoices(invs);
      } catch { /* non-fatal */ }

      try {
        const [files, folders] = await Promise.all([listClientOwnFiles(), listClientOwnFolders()]);
        setClientFiles(files);
        setClientFolders(folders);
      } catch { /* non-fatal */ }

      try {
        const [checkInsRes] = await Promise.all([
          fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.pending(clientId)}`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (checkInsRes.ok) {
          const checkInsData = await checkInsRes.json();
          const allPending = checkInsData.questionnaires || [];
          setPendingCheckInsCount(allPending.filter((q: any) => q.source !== 'form').length);
          setPendingFormsCount(allPending.filter((q: any) => q.source === 'form').length);
        }
      } catch { /* non-fatal */ }

      try {
        const enrollRes = await fetch(`${config.apiBaseUrl}${endpoints.main.packages.clientActive}`, { headers: { Authorization: `Bearer ${token}` } });
        if (enrollRes.ok) {
          const enrollData = await enrollRes.json();
          setActiveEnrollments(Array.isArray(enrollData) ? enrollData : []);
        }
      } catch { /* non-fatal */ }

      try {
        const pendingPayRes = await fetch(`${config.apiBaseUrl}${endpoints.main.packages.clientPendingPayments}`, { headers: { Authorization: `Bearer ${token}` } });
        if (pendingPayRes.ok) {
          const ppData = await pendingPayRes.json();
          setPendingPackagePayments(Array.isArray(ppData) ? ppData : []);
        }
      } catch { /* non-fatal */ }

      try {
        const vsRes = await fetch(`${config.apiBaseUrl}/verify/status`, { headers });
        if (vsRes.ok) setClientVerifyStatus(await vsRes.json());
      } catch { /* non-fatal */ }

      try {
        const meRes = await fetch(`${config.apiBaseUrl}/clients/me`, { headers });
        if (meRes.ok) {
          const me = await meRes.json();
          setAiConsent(me.ai_consent ?? null);
          setTermsAccepted(me.terms_accepted ?? null);
          setPrivacyAccepted(me.privacy_policy_accepted ?? null);
        }
      } catch { /* non-fatal */ }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);
  // Silently refresh data whenever the user switches sections so stale data doesn't linger
  useEffect(() => { fetchData(); }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Past sessions fetch — re-runs only when clientId or date range changes
  useEffect(() => {
    if (!clientId) return;
    setPastLoading(true);
    const token = localStorage.getItem('authToken') || '';
    const params = new URLSearchParams({
      include_payments: 'true',
      date_from: pastFrom,
      date_to: `${pastTo}T23:59:59Z`,
    });
    fetch(`${config.apiBaseUrl}${endpoints.main.clients.sessions(clientId)}?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const appts: ClientAppointment[] = (data.appointments ?? []).filter(
          (a: ClientAppointment) => new Date(a.scheduled_at) < new Date()
        );
        setPastSessions(appts);
        const merged: Record<string, AppointmentPaymentStatus> = {};
        (data.appointments ?? []).forEach((a: any) => {
          if (a.id && a.payment_status) merged[a.id] = { appointment_id: a.id, ...a.payment_status };
        });
        setPaymentStatuses(prev => ({ ...prev, ...merged }));
      })
      .catch(() => setPastSessions([]))
      .finally(() => setPastLoading(false));
  }, [clientId, pastFrom, pastTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const _startOfToday = new Date(); _startOfToday.setHours(0, 0, 0, 0);
  const upcomingAppointments = appointments
    .filter(a => a.status === 'scheduled' && new Date(a.scheduled_at) >= _startOfToday)
    .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());

  const pastAppointments = appointments.filter(
    a => a.status !== 'scheduled' || new Date(a.scheduled_at) < _startOfToday
  );

  const todayDateStr = new Date().toLocaleDateString('en-CA', { timeZone: timezone ?? undefined });
  const _weekEnd = new Date(); _weekEnd.setDate(_weekEnd.getDate() + (6 - _weekEnd.getDay()));
  const thisWeekEndStr = _weekEnd.toLocaleDateString('en-CA', { timeZone: timezone ?? undefined });
  const getApptDate = (a: ClientAppointment) =>
    new Date(a.scheduled_at).toLocaleDateString('en-CA', { timeZone: timezone ?? undefined });
  const todaySessions = upcomingAppointments.filter(a => getApptDate(a) === todayDateStr);
  const thisWeekSessions = upcomingAppointments.filter(a => { const d = getApptDate(a); return d > todayDateStr && d <= thisWeekEndStr; });
  const futureSessions = upcomingAppointments.filter(a => getApptDate(a) > thisWeekEndStr);

  const pendingPackets = packets.filter(p => p.status === 'pending');

  const navItems: { key: Section; label: string; Icon: React.ElementType; badge?: number }[] = [
    { key: 'home',     label: 'Home',     Icon: HomeIcon },
    { key: 'sessions', label: 'Sessions', Icon: CalendarIcon },
    {
      key: 'action-items',
      label: 'Action Items',
      Icon: ClipboardDocumentListIcon,
      badge: (dashboard?.action_items_summary?.overdue_count || 0) > 0
        ? dashboard?.action_items_summary?.overdue_count
        : undefined,
    },
    {
      key: 'forms',
      label: 'Forms',
      Icon: DocumentTextIcon,
      badge: pendingFormsCount > 0 ? pendingFormsCount : undefined,
    },
    {
      key: 'check-ins',
      label: 'Check-ins',
      Icon: ChartBarIcon,
      badge: pendingCheckInsCount > 0 ? pendingCheckInsCount : undefined,
    },
    { key: 'notes',     label: 'Notes',     Icon: BookOpenIcon },
    {
      key: 'documents',
      label: 'Documents',
      Icon: DocumentIcon,
      badge: pendingPackets.length > 0 ? pendingPackets.length : undefined,
    },
    { key: 'settings', label: 'Settings', Icon: Cog6ToothIcon },
  ];

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  // ── Section components ──────────────────────────────────────────────────────

  const coaches = dashboard?.coaches ?? [];

  const CoachFilterChips = () => {
    if (coaches.length < 2) return null;
    return (
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <button
          onClick={() => setCoachFilter(null)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${coachFilter === null ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
        >
          All coaches
        </button>
        {coaches.map(c => (
          <button
            key={c.id}
            onClick={() => setCoachFilter(prev => prev === c.id ? null : c.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${coachFilter === c.id ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
          >
            <span className="w-4 h-4 rounded-full bg-blue-900/60 flex items-center justify-center text-[9px] font-bold text-blue-300">
              {c.first_name[0]}{c.last_name[0]}
            </span>
            {c.first_name} {c.last_name}
          </button>
        ))}
      </div>
    );
  };

  const HomeSection = () => {
    const coaches = dashboard?.coaches ?? [];
    const cid = coachFilter;

    const invoiceByAppt: Record<string, Invoice> = Object.fromEntries(
      clientInvoices
        .filter(inv => inv.appointment_id && ['sent', 'viewed', 'overdue'].includes(inv.status))
        .map(inv => [inv.appointment_id!, inv])
    );

    const filteredUpcoming = upcomingAppointments.filter(a => !cid || (a as any).coach?.id === cid);
    const filteredToday  = filteredUpcoming.filter(a => getApptDate(a) === todayDateStr);
    const filteredWeek   = filteredUpcoming.filter(a => { const d = getApptDate(a); return d > todayDateStr && d <= thisWeekEndStr; });
    const filteredFuture = filteredUpcoming.filter(a => getApptDate(a) > thisWeekEndStr);

    const unpaidPast = pastAppointments.filter(a => {
      if (cid && (a as any).coach?.id !== cid) return false;
      const ps = paymentStatuses[a.id];
      if (!ps) return false;
      return ps.status === 'unpaid' || ps.status === 'failed' || ps.status === 'pending';
    });

    const sessionsThisWeek = filteredToday.length + filteredWeek.length;
    const amountDueCents = unpaidPast.reduce((sum, a) => sum + (paymentStatuses[a.id]?.pricing_rate_cents ?? 0), 0);

    return (
    <div className="p-8 space-y-6">
      {/* Greeting */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">{getGreeting()}, {firstName}!</h1>
          <p className="text-sm text-slate-400 mt-1">
            {formatDateTimeInTimezone(new Date().toISOString(), timezone, {
              weekday: 'long', month: 'long', day: 'numeric',
            })}
          </p>
        </div>
        {dashboard?.coach?.scheduling_type === 'google' && (
          aiConsent === null ? (
            <div className="flex-shrink-0 text-right">
              <button disabled className="px-4 py-2 bg-slate-700 text-slate-400 text-sm font-medium rounded-xl cursor-not-allowed">
                Book Session
              </button>
              <p className="text-xs text-amber-400 mt-1">Review AI consent in Settings first</p>
            </div>
          ) : (
            <button
              onClick={() => setShowBookModal(true)}
              className="flex-shrink-0 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Book Session
            </button>
          )
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-900/20 border border-red-800/30 rounded-xl text-red-300 text-sm">{error}</div>
      )}

      {/* Account setup card — shown only while there are incomplete items */}
      {(() => {
        const emailNeeded = clientVerifyStatus && !clientVerifyStatus.is_email_verified;
        const aiConsentNeeded = aiConsent === null;
        const termsNeeded = termsAccepted === false || termsAccepted === null;
        const privacyNeeded = privacyAccepted === false || privacyAccepted === null;
        const phoneNeeded = clientVerifyStatus?.is_email_verified && clientVerifyStatus.has_phone && !clientVerifyStatus.is_phone_verified;
        const agreementNeeded = pendingPackets.length > 0;
        const hasAnyIncomplete = emailNeeded || aiConsentNeeded || termsNeeded || privacyNeeded || phoneNeeded || agreementNeeded;
        if (!hasAnyIncomplete) return null;
        const hasBlockers = !!(emailNeeded || aiConsentNeeded);
        const items = [
          emailNeeded    && { required: true,  label: 'Verify your email',         note: null, action: () => { window.location.href = '/verify'; } },
          aiConsentNeeded && { required: true, label: 'Review AI session consent',  note: 'Your choice is needed before your first session.', action: () => { setConsentChoice(null); setShowConsentModal(true); } },
          termsNeeded    && { required: false, label: 'Accept the Terms of Use',    note: null, action: () => { setActiveSection('settings'); setSettingsSubSection('ai'); } },
          privacyNeeded  && { required: false, label: 'Accept the Privacy Policy',  note: null, action: () => { setActiveSection('settings'); setSettingsSubSection('privacy'); } },
          phoneNeeded    && { required: false, label: 'Verify your phone number',   note: 'Required to enable SMS notifications.', action: () => { setActiveSection('settings'); setSettingsSubSection('profile'); } },
          agreementNeeded && { required: false, label: 'Sign your agreement',       note: `${pendingPackets.length} agreement${pendingPackets.length > 1 ? 's' : ''} awaiting your signature.`, action: () => setActiveSection('documents') },
        ].filter(Boolean) as { required: boolean; label: string; note: string | null; action: () => void }[];
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
            <button
              onClick={() => setSetupCardOpen(o => !o)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${hasBlockers ? 'bg-red-500' : 'bg-amber-400'}`} />
                <span className="text-sm font-medium text-white">Account setup</span>
                <span className="text-xs text-slate-400">{items.length} item{items.length > 1 ? 's' : ''} remaining</span>
              </div>
              <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${setupCardOpen ? 'rotate-180' : ''}`} />
            </button>
            {setupCardOpen && (
              <ul className="divide-y divide-slate-700 border-t border-slate-700">
                {items.map(({ required, label, note, action }) => (
                  <li key={label} className="flex items-start gap-3 px-5 py-2.5">
                    <div className={`w-3.5 h-3.5 mt-0.5 rounded-full border-2 flex-shrink-0 ${required ? 'border-red-500' : 'border-amber-500/60'}`} />
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm ${required ? 'text-red-400' : 'text-amber-400/80'}`}>{label}</span>
                      {note && <p className="text-xs text-slate-500 mt-0.5">{note}</p>}
                    </div>
                    <button onClick={action} className="text-xs text-blue-400 hover:text-blue-300 flex-shrink-0 mt-0.5">Set up →</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })()}

      {/* Coach filter chips — only shown when client has multiple coaches */}
      {coaches.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setCoachFilter(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              coachFilter === null
                ? 'bg-blue-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            All coaches
          </button>
          {coaches.map(c => (
            <button
              key={c.id}
              onClick={() => setCoachFilter(prev => prev === c.id ? null : c.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                coachFilter === c.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <span className="w-4 h-4 rounded-full bg-blue-900/60 flex items-center justify-center text-[9px] font-bold text-blue-300">
                {c.first_name[0]}{c.last_name[0]}
              </span>
              {c.first_name} {c.last_name}
            </button>
          ))}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className={`rounded-xl px-4 py-4 border ${sessionsThisWeek > 0 ? 'bg-blue-900/20 border-blue-800/40' : 'bg-slate-800 border-slate-700'}`}>
          <CalendarIcon className="w-4 h-4 text-slate-500 mb-2" />
          <p className="text-2xl font-bold text-white">{sessionsThisWeek}</p>
          <p className="text-xs text-slate-400 mt-1">Sessions this week</p>
        </div>
        {(() => {
          const byType = ((dashboard?.action_items_summary as any)?.pending_by_type ?? []) as Array<{type: string; coach_id: string; count: number}>;
          const typeCount = (t: string) => byType
            .filter(r => r.type === t && (!cid || r.coach_id === cid))
            .reduce((s, r) => s + r.count, 0);
          const cardTotal = typeCount('task') + typeCount('form') + typeCount('check-in') + typeCount('document');
          return (
            <div className={`rounded-xl px-4 py-4 border ${cardTotal > 0 ? 'bg-amber-900/20 border-amber-800/40' : 'bg-slate-800 border-slate-700'}`}>
              <ClipboardDocumentListIcon className="w-4 h-4 text-slate-500 mb-2" />
              <p className="text-xs text-slate-400 mb-2">Action items pending</p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                {([
                  { label: 'Tasks',     key: 'task' },
                  { label: 'Forms',     key: 'form' },
                  { label: 'Check-ins', key: 'check-in' },
                  { label: 'Docs',      key: 'document' },
                ] as const).map(({ label, key }) => {
                  const count = typeCount(key);
                  return (
                    <div key={key} className="flex items-center justify-between gap-1">
                      <span className="text-xs text-slate-500">{label}</span>
                      <button
                        onClick={() => setActiveSection('action-items')}
                        className={`text-sm font-bold transition-colors ${count > 0 ? 'text-amber-400 hover:text-amber-300' : 'text-slate-600 hover:text-slate-400'}`}
                      >
                        {count}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        <div className={`rounded-xl px-4 py-4 border ${amountDueCents > 0 ? 'bg-red-900/20 border-red-800/40' : 'bg-slate-800 border-slate-700'}`}>
          <BanknotesIcon className="w-4 h-4 text-slate-500 mb-2" />
          <p className={`text-2xl font-bold ${amountDueCents > 0 ? 'text-red-400' : 'text-white'}`}>
            ${Math.round(amountDueCents / 100)}
          </p>
          <p className="text-xs text-slate-400 mt-1">Amount due</p>
        </div>
      </div>

      {/* 2-up grid: left column (Action Items + Pending Payments) | right column (Sessions) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

      {/* Left column */}
      <div className="flex flex-col gap-4">

      {/* Pending Payments box */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-700">
          <h2 className="text-sm font-semibold text-gray-200">Pending Payments</h2>
        </div>
        {appointmentsLoading ? (
          <div className="px-5 py-6 text-xs text-slate-500">Loading…</div>
        ) : unpaidPast.length === 0 && pendingPackagePayments.length === 0 ? (
          <div className="px-5 py-6 text-xs text-slate-500">No pending payments.</div>
        ) : (
          <div className="divide-y divide-slate-700">
            {unpaidPast.map(appt => {
              const ps = paymentStatuses[appt.id];
              const amountCents = ps?.pricing_rate_cents;
              const invoice = invoiceByAppt[appt.id];
              return (
                <div key={appt.id} className="px-5 py-3.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-200 truncate">{appt.meeting_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {(appt as any).coach?.first_name} {(appt as any).coach?.last_name} · {formatDateTimeInTimezone(appt.scheduled_at, timezone, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  {amountCents != null && (
                    <span className="text-sm font-semibold text-white flex-shrink-0">
                      ${Math.round(amountCents / 100)}
                    </span>
                  )}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {ps?.status === 'pending' && (
                      <span className="text-xs font-medium text-red-400">Pending</span>
                    )}
                    {invoice && (
                      <a href={invoice.payment_url} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-medium rounded-lg transition-colors">
                        Invoice
                      </a>
                    )}
                    <button onClick={() => setPayingAppointment(appt)}
                      className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors">
                      {ps?.status === 'failed' ? 'Retry' : 'Pay'}
                    </button>
                  </div>
                </div>
              );
            })}
            {pendingPackagePayments.map(pp => {
              const label = pp.payment_type === 'installment' && pp.installment_number != null
                ? `Installment ${pp.installment_number}`
                : pp.payment_type === 'setup_fee' ? 'Setup Fee' : 'Payment';
              const dueStr = pp.due_date
                ? new Date(pp.due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                : null;
              return (
                <div key={pp.id} className="px-5 py-3.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-200 truncate">{pp.package_name} — {label}</p>
                    {dueStr && <p className="text-xs text-slate-500 mt-0.5">Due {dueStr}</p>}
                  </div>
                  <span className="text-sm font-semibold text-white flex-shrink-0">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: pp.currency.toUpperCase(), minimumFractionDigits: 0 }).format(pp.amount_cents / 100)}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs font-medium ${pp.status === 'failed' ? 'text-orange-400' : 'text-red-400'}`}>
                      {pp.status === 'failed' ? 'Failed' : 'Pending'}
                    </span>
                    {pp.payment_url && (
                      <a
                        href={pp.payment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-medium rounded-lg transition-colors"
                      >
                        Invoice
                      </a>
                    )}
                    <button
                      onClick={() => setPayingPackagePayment({ id: pp.id, package_name: pp.package_name, label })}
                      className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
                    >
                      Pay
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      </div>{/* end left column */}

      {/* Right column: Sessions + Package Enrollments */}
      <div className="flex flex-col gap-4">

      {/* Sessions box */}
      {(() => {
        const homeGroups: { key: string; label: string; sessions: ClientAppointment[] }[] = [
          { key: 'h-today',  label: 'Today',     sessions: filteredToday },
          { key: 'h-week',   label: 'This Week', sessions: filteredWeek },
          { key: 'h-future', label: 'Future',    sessions: filteredFuture },
        ].filter(g => g.sessions.length > 0);
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-200">Sessions</h2>
              <button onClick={() => setActiveSection('sessions')} className="text-xs text-blue-400 hover:text-blue-300">
                View all →
              </button>
            </div>
            {appointmentsLoading ? (
              <div className="px-5 py-6 text-xs text-slate-500">Loading…</div>
            ) : homeGroups.length === 0 ? (
              <div className="px-5 py-6 text-xs text-slate-500">No upcoming sessions.</div>
            ) : (
              <div className="px-4 py-3 space-y-4">
                {homeGroups.map(({ key, label, sessions: grpSessions }) => {
                  const isOpen = sessionsOpen[key] === true;
                  return (
                    <div key={key}>
                      <button type="button"
                        onClick={() => setSessionsOpen(prev => ({ ...prev, [key]: !isOpen }))}
                        className="flex items-center gap-1.5 mb-2 w-full text-left"
                      >
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {label} <span className="font-normal normal-case">({grpSessions.length})</span>
                        </p>
                        <ChevronRightIcon className={`w-3.5 h-3.5 text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="space-y-1.5">
                          {grpSessions.map(s => {
                            const dt = new Date(s.scheduled_at);
                            const timeStr = dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: timezone ?? undefined });
                            const dateStr = label !== 'Today'
                              ? dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: timezone ?? undefined })
                              : null;
                            return (
                              <div key={s.id} className="flex items-center justify-between py-3 px-3 rounded-lg border border-slate-700 hover:bg-slate-700/40 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="flex-shrink-0 text-right w-16">
                                    <span className="text-sm font-semibold text-white block">{timeStr}</span>
                                    {dateStr && <span className="text-xs text-slate-500">{dateStr}</span>}
                                  </div>
                                  <div className="w-px h-7 bg-slate-700 flex-shrink-0" />
                                  <VideoCameraIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-white truncate">{s.meeting_name}</p>
                                    <p className="text-xs text-slate-500">{s.coach.first_name} {s.coach.last_name} · {s.duration_minutes} min</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-2 flex-wrap justify-end">
                                  {/* Payment status */}
                                  {(() => {
                                    const ps = paymentStatuses[s.id];
                                    if (ps?.status === 'succeeded') return (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-900/40 text-green-300 font-medium">Paid</span>
                                    );
                                    if (ps?.status === 'unpaid') return (
                                      <span className="text-xs text-slate-500">Due</span>
                                    );
                                    if (ps?.status === 'failed') return (
                                      <button onClick={() => setPayingAppointment(s)}
                                        className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 rounded-lg transition-colors">
                                        Retry
                                      </button>
                                    );
                                    return null;
                                  })()}
                                  {s.meeting_url && (
                                    <button
                                      onClick={() => window.open(s.meeting_url!, '_blank', 'noopener,noreferrer')}
                                      className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded-lg transition-colors">
                                      <VideoCameraIcon className="w-3.5 h-3.5" />
                                      Join
                                    </button>
                                  )}
                                  {s.has_summary && (
                                    <button onClick={() => setSelectedAppointment(s)} className="text-xs text-blue-400 hover:text-blue-300">
                                      Notes
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Package Enrollments */}
      {activeEnrollments.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-700">
            <h2 className="text-sm font-semibold text-gray-200">Package Enrollments</h2>
          </div>
          <div className="divide-y divide-slate-700">
            {activeEnrollments.map(enr => {
              const sessionsLeft = enr.sessions_total - enr.sessions_used;
              const progressPct = enr.sessions_total > 0 ? (enr.sessions_used / enr.sessions_total) * 100 : 0;
              const cadenceMap: Record<string, string> = { weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly', custom: 'Custom' };
              return (
                <div key={enr.id} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-200">{enr.package_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {enr.session_duration_minutes != null && `${enr.session_duration_minutes} min · `}
                        {enr.cadence ? (cadenceMap[enr.cadence] ?? enr.cadence) + ' · ' : ''}
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: (enr.currency || 'USD').toUpperCase(), minimumFractionDigits: 0 }).format((enr.price_cents ?? 0) / 100)}
                      </p>
                    </div>
                    {enr.sessions_total > 0 && (
                      <span className="text-xs bg-blue-900/40 text-blue-300 px-2.5 py-1 rounded-full font-medium flex-shrink-0">
                        {sessionsLeft} session{sessionsLeft !== 1 ? 's' : ''} left
                      </span>
                    )}
                  </div>
                  {enr.sessions_total > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-slate-700 rounded-full h-1.5">
                        <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, progressPct)}%` }} />
                      </div>
                      <span className="text-xs text-slate-500 flex-shrink-0">{enr.sessions_used} / {enr.sessions_total}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      </div>{/* end right column */}

      </div>{/* end grid */}

      {/* AI opted-out compact notice */}
      {aiConsent === false && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-900/20 border border-amber-700/30 rounded-xl">
          <ExclamationCircleIcon className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-400 flex-1">AI session processing is off. Session summaries, action items and nudges are unavailable.</span>
          <button onClick={() => { setConsentChoice(null); setShowConsentModal(true); }} className="text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap">Change →</button>
        </div>
      )}

      {/* AI Consent Modal */}
      {showConsentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
            <div>
              <h2 className="text-lg font-bold text-gray-100">AI-powered coaching — your choice</h2>
              <p className="text-sm text-slate-400 mt-1">This takes 60 seconds to read and is worth it.</p>
            </div>

            {/* What you unlock */}
            <div className="bg-blue-900/30 border border-blue-800/30 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-blue-300 uppercase tracking-wide">What AI enables for you</p>
              <ul className="space-y-1.5 text-sm text-blue-200">
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Session summaries</strong> — structured notes from every session, available to you and your coach.</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Action items</strong> — automatically captured and tracked between sessions so nothing falls through.</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Progress tracking</strong> — check-ins and trends that show how far you've come.</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Smart nudges</strong> — timely reminders on your action items so momentum doesn't stall.</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">✓</span><span><strong>Prepared coach</strong> — your coach gets an AI briefing before each session so you spend less time catching up.</span></li>
              </ul>
            </div>

            {/* How it works + privacy */}
            <div className="space-y-3 text-sm text-slate-300">
              <p className="font-medium text-gray-200">How it works</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span>Sessions are recorded via <strong>Recall.ai</strong> and transcribed automatically. Transcripts are permanently deleted after 30 days.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span><strong>Your name is never sent to AI.</strong> Before any transcript reaches Claude, your name is replaced with <code className="bg-slate-700 px-1 rounded text-xs">[CLIENT]</code> — Anthropic never sees who you are.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span><strong>Anthropic does not train on your data.</strong> Session content is never used to train AI models. <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Read Anthropic's privacy policy →</a></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span>Session summaries are visible only to you and your coach. Your data is never sold or used for advertising.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0 mt-1.5" />
                  <span>You can change this decision at any time from your dashboard.</span>
                </li>
              </ul>
            </div>

            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${consentChoice === true ? 'border-blue-500 bg-blue-900/30' : 'border-slate-600 hover:border-slate-500'}`}>
                <input type="radio" name="consent" checked={consentChoice === true} onChange={() => setConsentChoice(true)} className="mt-0.5 accent-blue-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-100">I consent — enable AI features</p>
                  <p className="text-xs text-slate-400 mt-0.5">Session recording, AI summarization, action items, nudges, and progress tracking.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${consentChoice === false ? 'border-slate-500 bg-slate-700' : 'border-slate-600 hover:border-slate-500'}`}>
                <input type="radio" name="consent" checked={consentChoice === false} onChange={() => setConsentChoice(false)} className="mt-0.5 accent-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-300">I do not consent — manual only</p>
                  <p className="text-xs text-slate-400 mt-0.5">No recording or AI processing. Your coach will take manual notes.</p>
                </div>
              </label>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={async () => {
                  if (consentChoice === null) return;
                  setConsentSubmitting(true);
                  try {
                    const token = localStorage.getItem('authToken') || '';
                    const res = await fetch(`${config.apiBaseUrl}/clients/me/ai-consent`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ consent: consentChoice }),
                    });
                    if (res.ok) {
                      setAiConsent(consentChoice);
                      setShowConsentModal(false);
                    }
                  } finally {
                    setConsentSubmitting(false);
                  }
                }}
                disabled={consentSubmitting || consentChoice === null}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl transition-colors"
              >
                {consentSubmitting ? 'Saving…' : 'Save my choice'}
              </button>
              <button
                onClick={() => setShowConsentModal(false)}
                className="px-4 py-2.5 border border-slate-600 text-slate-300 hover:bg-slate-700 text-sm font-medium rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}




      {/* Pay Your Coach card — only shown if coach has link accounts configured */}
      {coachLinkAccounts.length > 0 && (
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-5">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-3">Pay Your Coach</p>
          <div className="flex flex-wrap gap-2">
            {coachLinkAccounts.map(account => {
              const labels: Record<string, string> = {
                paypal: 'Pay with PayPal',
                venmo: 'Pay with Venmo',
                cashapp: 'Pay with Cash App',
                zelle: 'Pay with Zelle',
              };
              const colors: Record<string, string> = {
                paypal:  'bg-blue-600 hover:bg-blue-700',
                venmo:   'bg-sky-500 hover:bg-sky-600',
                cashapp: 'bg-green-600 hover:bg-green-700',
                zelle:   'bg-violet-600 hover:bg-violet-700',
              };
              const label = labels[account.provider] ?? `Pay via ${account.provider}`;
              const color = colors[account.provider] ?? 'bg-gray-600 hover:bg-gray-700';
              return account.payment_url ? (
                <a
                  key={account.provider}
                  href={account.payment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm font-medium text-white px-4 py-2 rounded-xl transition-colors ${color}`}
                >
                  {label}
                </a>
              ) : (
                <div key={account.provider} className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2">
                  <span className="font-medium">{label.replace('Pay with ', '')}:</span>{' '}
                  <span className="font-mono">{account.handle}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
  };

  const SessionsSection = () => {
    const coaches = dashboard?.coaches ?? [];
    const [sessCoachFilter, setSessCoachFilter] = React.useState<string | null>(null);
    const scid = sessCoachFilter;

    const byCoach = (arr: ClientAppointment[]) =>
      scid ? arr.filter(a => a.coach.id === scid) : arr;

    const groups = [
      { key: 'today',  label: 'Today',         sessions: byCoach(todaySessions),    loading: false },
      { key: 'week',   label: 'This Week',      sessions: byCoach(thisWeekSessions), loading: false },
      { key: 'future', label: 'Future',         sessions: byCoach(futureSessions),   loading: false },
      { key: 'past',   label: 'Past Sessions',  sessions: byCoach(pastSessions),     loading: pastLoading },
    ].filter(g => g.sessions.length > 0 || g.key === 'past');

    const meetingStatusBadge = (status: string) => {
      const map: Record<string, string> = {
        scheduled:  'bg-blue-900/40 text-blue-300',
        completed:  'bg-green-900/40 text-green-300',
        cancelled:  'bg-slate-700 text-slate-400',
        no_show:    'bg-orange-900/40 text-orange-300',
      };
      return map[status] ?? 'bg-slate-700 text-slate-400';
    };

    const renderCard = (appt: ClientAppointment, showDate: boolean) => {
      const dt = new Date(appt.scheduled_at);
      const timeStr = dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: timezone ?? undefined });
      const dateStr = showDate
        ? dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: timezone ?? undefined })
        : null;
      const ps = paymentStatuses[appt.id];
      const isFuture = new Date(appt.scheduled_at) > new Date();
      return (
        <div key={appt.id} className="py-3 px-4 rounded-lg border border-slate-700 hover:bg-slate-700/40 transition-colors">
          <div className="flex items-start justify-between gap-3">
            {/* Left: time + name */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 text-right w-20">
                <span className="text-sm font-semibold text-white block">{timeStr}</span>
                {dateStr && <span className="text-xs text-slate-500">{dateStr}</span>}
              </div>
              <div className="w-px h-7 bg-slate-700 flex-shrink-0" />
              <VideoCameraIcon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{appt.meeting_name}</p>
                <p className="text-xs text-slate-500">{appt.coach.first_name} {appt.coach.last_name} · {appt.duration_minutes} min</p>
              </div>
            </div>

            {/* Right: actions */}
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
              {/* Meeting status */}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${meetingStatusBadge(appt.status)}`}>
                {appt.status.replace('_', ' ')}
              </span>

              {/* Payment status / action — shown for all sessions */}
              {ps?.status === 'succeeded' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-900/40 text-green-300 font-medium">Paid</span>
              )}
              {ps?.status === 'pending' && (
                <span className="text-xs font-medium text-red-400">Pending</span>
              )}
              {ps?.status === 'processing' && (
                <span className="text-xs text-slate-400">Processing…</span>
              )}
              {(ps?.status === 'waived' || ps?.status === 'manual') && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-400 font-medium capitalize">{ps.status}</span>
              )}
              {ps?.status === 'refunded' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-400 font-medium">Refunded</span>
              )}
              {/* Pay action only for past sessions that are unpaid/failed */}
              {(!ps || ps.status === 'unpaid' || ps.status === 'failed') && !isFuture && (
                <button onClick={() => setPayingAppointment(appt)}
                  className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 rounded-lg transition-colors">
                  {ps?.status === 'failed' ? 'Retry' : 'Pay'}
                </button>
              )}
              {/* Future sessions with no rate show nothing extra; with a rate but not yet paid show a soft indicator */}
              {isFuture && ps?.status === 'unpaid' && (
                <span className="text-xs text-slate-500">Due</span>
              )}

              {/* Notes */}
              {appt.has_summary ? (
                <button onClick={() => setSelectedAppointment(appt)}
                  className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2">
                  Notes
                </button>
              ) : (
                <span className="text-xs text-slate-600">No notes</span>
              )}

              {/* Join — shown for all future sessions */}
              {isFuture && (
                <button
                  onClick={() => window.open(appt.meeting_url!, '_blank', 'noopener,noreferrer')}
                  className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded-lg transition-colors"
                >
                  <VideoCameraIcon className="w-3.5 h-3.5" />
                  Join
                </button>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
    <div className="p-8 max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-100">Sessions</h1>
        {dashboard?.coach?.scheduling_type === 'google' && (
          <button
            onClick={() => setShowBookModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors"
          >
            + Schedule Session
          </button>
        )}
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-4">
        {coaches.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setSessCoachFilter(null)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${scid === null ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            >
              All coaches
            </button>
            {coaches.map(c => (
              <button
                key={c.id}
                onClick={() => setSessCoachFilter(prev => prev === c.id ? null : c.id)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${scid === c.id ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
              >
                <span className="w-4 h-4 rounded-full bg-slate-500 flex items-center justify-center text-[10px] font-bold">
                  {c.first_name[0]}{c.last_name[0]}
                </span>
                {c.first_name} {c.last_name}
              </button>
            ))}
          </div>
        )}

        {/* Past sessions date range */}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-slate-500 whitespace-nowrap">Past sessions:</span>
          <input
            type="date"
            value={pastFrom}
            max={pastTo}
            onChange={e => setPastFrom(e.target.value)}
            className="text-xs bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5 text-slate-300 focus:outline-none focus:border-blue-500"
          />
          <span className="text-xs text-slate-500">to</span>
          <input
            type="date"
            value={pastTo}
            min={pastFrom}
            max={new Date().toISOString().slice(0, 10)}
            onChange={e => setPastTo(e.target.value)}
            className="text-xs bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5 text-slate-300 focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {appointmentsLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center justify-between py-3 px-4 rounded-lg border border-slate-700 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-20 h-8 bg-slate-700 rounded" />
                <div className="w-px h-7 bg-slate-700" />
                <div className="space-y-1.5">
                  <div className="h-4 bg-slate-700 rounded w-48" />
                  <div className="h-3 bg-slate-700/50 rounded w-28" />
                </div>
              </div>
              <div className="h-6 bg-slate-700 rounded-full w-20" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map(({ key, label, sessions: grp, loading: grpLoading }) => {
            const isOpen = sessionGroupOpen[key] === true;
            const isPast = key === 'past';
            return (
              <div key={key}>
                <button
                  type="button"
                  onClick={() => setSessionGroupOpen(prev => ({ ...prev, [key]: !isOpen }))}
                  className="flex items-center gap-1.5 mb-2 w-full text-left"
                >
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {label}{' '}
                    {grpLoading
                      ? <span className="font-normal normal-case">(loading…)</span>
                      : <span className="font-normal normal-case">({grp.length})</span>
                    }
                  </p>
                  <ChevronRightIcon className={`w-3.5 h-3.5 text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </button>
                {isOpen && (
                  grpLoading ? (
                    <div className="space-y-1.5">
                      {[1, 2].map(i => (
                        <div key={i} className="h-14 rounded-lg border border-slate-700 animate-pulse bg-slate-800/50" />
                      ))}
                    </div>
                  ) : grp.length === 0 ? (
                    <p className="text-xs text-slate-600 px-1 pb-2">No sessions in this date range.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {grp.map(appt => renderCard(appt, isPast || key === 'future'))}
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
    );
  };

  const InvoicesSection = () => {
    const [invoices, setInvoices] = React.useState<Invoice[]>([]);
    const [loadingInv, setLoadingInv] = React.useState(true);
    const [statusFilter, setStatusFilter] = React.useState<'all' | 'unpaid' | 'paid'>('all');
    const [symbolMap, setSymbolMap] = React.useState<SymbolMap>(FALLBACK_SYMBOL_MAP);

    React.useEffect(() => {
      listMyInvoices().then(setInvoices).catch(() => {}).finally(() => setLoadingInv(false));
      fetch(`${config.apiBaseUrl}/countries`)
        .then(r => r.ok ? r.json() : [])
        .then((countries: ApiCountry[]) => setSymbolMap(buildSymbolMap(countries)))
        .catch(() => {});
    }, []);

    const STATUS_BADGE: Record<string, string> = {
      sent:    'bg-blue-900/40 text-blue-300 border-blue-800/50',
      viewed:  'bg-purple-900/40 text-purple-300 border-purple-800/50',
      paid:    'bg-green-900/40 text-green-300 border-green-800/50',
      overdue: 'bg-red-900/40 text-red-300 border-red-800/50',
      void:    'bg-gray-700 text-gray-500 border-gray-700',
    };

    const UNPAID_STATUSES = ['sent', 'viewed', 'overdue'];

    const filteredInvoices = invoices.filter(inv => {
      if (coachFilter && inv.coach_id !== coachFilter) return false;
      if (statusFilter === 'unpaid') return UNPAID_STATUSES.includes(inv.status);
      if (statusFilter === 'paid')   return inv.status === 'paid';
      return true;
    });

    const copyLink = (url: string) => navigator.clipboard.writeText(url);

    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-gray-100 mb-4">Invoices</h1>
        <CoachFilterChips />
        <div className="flex items-center gap-2 mb-4">
          {(['all', 'unpaid', 'paid'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${
                statusFilter === s ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {s === 'all' ? 'All' : s === 'unpaid' ? 'Unpaid' : 'Paid'}
            </button>
          ))}
        </div>
        {loadingInv ? (
          <p className="text-slate-400 text-sm">Loading…</p>
        ) : filteredInvoices.length === 0 ? (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 text-center">
            <DocumentTextIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No invoices yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-dark-border">
                  <th className="pb-2 text-left font-medium">Invoice</th>
                  <th className="pb-2 text-left font-medium">Engagement Type</th>
                  <th className="pb-2 text-left font-medium">Name</th>
                  <th className="pb-2 text-left font-medium">Session Date</th>
                  <th className="pb-2 text-right font-medium pr-8">Amount</th>
                  <th className="pb-2 text-left font-medium pr-6">Status</th>
                  <th className="pb-2 text-left font-medium pr-4">Due Date</th>
                  <th className="pb-2 text-left font-medium">Date Paid</th>
                  <th className="pb-2 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {filteredInvoices.map(inv => {
                  const isPackage = !!inv.package_payment_id;
                  const engagementType = isPackage ? 'Package' : 'Session';
                  const engagementName = isPackage
                    ? (inv.payment_type === 'installment' && inv.installment_number != null
                        ? `${inv.package_name ?? 'Package'} — Installment ${inv.installment_number}`
                        : (inv.package_name ?? 'Package'))
                    : (inv.meeting_name ?? inv.description ?? '—');
                  const dueStr = inv.due_date
                    ? new Date(inv.due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—';
                  const paidStr = inv.paid_at
                    ? new Date(inv.paid_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                    : null;
                  const sessionStr = inv.session_date
                    ? new Date(inv.session_date).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                    : '—';
                  return (
                  <tr key={inv.id} className="text-gray-300 hover:bg-dark-hover/30">
                    <td className="py-2.5 pr-4 font-medium">
                      <a
                        href={inv.payment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 hover:underline"
                      >
                        {inv.invoice_number}
                      </a>
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className={`text-xs px-2 py-0.5 rounded border font-medium ${
                        isPackage
                          ? 'bg-purple-900/30 text-purple-300 border-purple-800/50'
                          : 'bg-blue-900/30 text-blue-300 border-blue-800/50'
                      }`}>
                        {engagementType}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-gray-300 max-w-[200px] truncate" title={engagementName}>{engagementName}</td>
                    <td className="py-2.5 pr-4 text-gray-400">{isPackage ? '—' : sessionStr}</td>
                    <td className="py-2.5 pr-8 text-right font-medium">{currencySymbol(inv.currency, symbolMap)}{(inv.amount_cents / 100).toFixed(2)}</td>
                    <td className="py-2.5 pr-6">
                      {inv.payment_status === 'failed' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full border bg-red-900/40 text-red-300 border-red-800/50">
                          Failed
                        </span>
                      ) : (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_BADGE[inv.status] ?? STATUS_BADGE.sent}`}>
                          {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-400">{dueStr}</td>
                    <td className="py-2.5 pr-4 text-gray-400">
                      {paidStr ? <span className="text-green-400">{paidStr}</span> : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="py-2.5">
                      {inv.status !== 'paid' && inv.status !== 'void' && (
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => copyLink(inv.payment_url)}
                            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-300 border border-dark-border rounded transition-colors"
                            title="Copy payment link"
                          >
                            Copy link
                          </button>
                          <a
                            href={inv.payment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-1 text-xs bg-blue-900/40 text-blue-300 border border-blue-800/50 rounded hover:bg-blue-800/60 transition-colors"
                          >
                            Pay →
                          </a>
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const ActionItemsSection = () => (
    <div className="p-8 max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-100 mb-4">Action Items</h1>
      <CoachFilterChips />
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
        <ActionItemsPanel
          mode="by_client"
          entityId={clientId}
          isCoach={false}
          coachIdFilter={coachFilter}
          approvedOnly
          onNavigate={(section) => setActiveSection(section as any)}
        />
      </div>
    </div>
  );

  const CheckInsSection = () => (
    <div className="p-8 max-w-3xl space-y-6">
      <h1 className="text-2xl font-bold text-gray-100">Check-ins</h1>
      <CheckInsTab clientId={clientId} onCountChange={count => setPendingCheckInsCount(count)} />
    </div>
  );

  // ── Notes section ─────────────────────────────────────────────────────────

  const NotesSection = () => {
    const [sessions, setSessions] = React.useState<any[]>([]);
    const [notesLoading, setNotesLoading] = React.useState(true);
    const [modalAppt, setModalAppt] = React.useState<any | null>(null);
    const [expandedMonths, setExpandedMonths] = React.useState<Set<string>>(new Set());

    const hdrs = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` });

    React.useEffect(() => {
      setNotesLoading(true);
      fetch(`${config.apiBaseUrl}${endpoints.main.clients.sessions(clientId)}`, { headers: hdrs() })
        .then(r => r.json())
        .then(data => {
          const withSummary = (data.appointments || [])
            .filter((a: any) => a.has_summary)
            .sort((a: any, b: any) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime());
          setSessions(withSummary);
          // Auto-expand the most recent month
          if (withSummary.length > 0) {
            const dt = new Date(withSummary[0].scheduled_at);
            const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
            setExpandedMonths(new Set([key]));
          }
        })
        .catch(() => {})
        .finally(() => setNotesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleMonth = (key: string) =>
      setExpandedMonths(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

    // Group by YYYY-MM
    const grouped: Record<string, any[]> = {};
    sessions.forEach(s => {
      const dt = new Date(s.scheduled_at);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(s);
    });
    const monthKeys = Object.keys(grouped).sort().reverse();

    const monthLabel = (key: string) => {
      const [y, m] = key.split('-');
      return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    };

    return (
      <div className="p-8 max-w-3xl space-y-4">
        <h1 className="text-2xl font-bold text-gray-100">Session Notes</h1>

        {notesLoading ? (
          <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
        ) : sessions.length === 0 ? (
          <div className="bg-slate-800 rounded-xl border border-slate-700 px-6 py-8 flex flex-col items-center text-center">
            <BookOpenIcon className="w-9 h-9 text-slate-500 mb-2" />
            <p className="text-sm font-semibold text-gray-300">No session notes yet</p>
            <p className="text-sm text-gray-500 mt-1 max-w-xs">Your coach will share notes after sessions are complete.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {monthKeys.map(mkey => (
              <div key={mkey} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                <button type="button" onClick={() => toggleMonth(mkey)}
                  className="w-full flex items-center justify-between px-4 py-3.5 text-left hover:bg-slate-700/30 transition-colors">
                  <div className="flex items-center gap-2">
                    <ChevronRightIcon className={`w-3.5 h-3.5 text-slate-500 transition-transform ${expandedMonths.has(mkey) ? 'rotate-90' : ''}`} />
                    <span className="text-sm font-semibold text-gray-200">{monthLabel(mkey)}</span>
                    <span className="text-xs text-gray-500">{grouped[mkey].length} session{grouped[mkey].length !== 1 ? 's' : ''}</span>
                  </div>
                </button>
                {expandedMonths.has(mkey) && (
                  <div className="border-t border-slate-700/60 divide-y divide-slate-700/40">
                    {grouped[mkey].map((s: any) => {
                      const dt = new Date(s.scheduled_at);
                      const dateStr = dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: timezone ?? undefined });
                      const timeStr = dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: timezone ?? undefined });
                      return (
                        <div key={s.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-700/20 transition-colors">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-200">{s.meeting_name || 'Session'}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{dateStr} · {timeStr}</p>
                          </div>
                          <button type="button" onClick={() => setModalAppt(s)}
                            title="View session notes"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-primary-400 hover:bg-primary-900/20 transition-colors flex-shrink-0">
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Notes modal */}
        {modalAppt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setModalAppt(null)} />
            <div className="relative z-10 w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 shrink-0">
                <div>
                  <p className="text-sm font-semibold text-gray-100">{modalAppt.meeting_name || 'Session'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {new Date(modalAppt.scheduled_at).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: timezone ?? undefined })}
                  </p>
                </div>
                <button onClick={() => setModalAppt(null)} className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="overflow-y-auto flex-1">
                <SessionSummaryPanel
                  appointmentId={modalAppt.id}
                  meetingName={modalAppt.meeting_name || 'Session'}
                  onClose={() => setModalAppt(null)}
                  role="client"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const PaymentsSection = () => {
    type PayStatus = 'no_rate' | 'unpaid' | 'pending' | 'succeeded' | 'failed' | 'refunded' | 'waived';
    const STATUS_OPTIONS: { value: PayStatus; label: string }[] = [
      { value: 'succeeded', label: 'Paid' },
      { value: 'unpaid',    label: 'Unpaid' },
      { value: 'pending',   label: 'Pending' },
      { value: 'failed',    label: 'Failed' },
      { value: 'refunded',  label: 'Refunded' },
      { value: 'waived',    label: 'Waived' },
    ];

    function statusBadge(s: string): { label: string; cls: string } {
      switch (s) {
        case 'succeeded':
        case 'manual':    return { label: 'Paid',        cls: 'bg-green-900/40 text-green-400 border-green-800' };
        case 'pending':   return { label: 'Pending',     cls: 'bg-yellow-900/40 text-yellow-400 border-yellow-800' };
        case 'unpaid':    return { label: 'Unpaid',      cls: 'bg-red-900/40 text-red-400 border-red-800' };
        case 'failed':    return { label: 'Failed',      cls: 'bg-red-900/40 text-red-400 border-red-800' };
        case 'refunded':  return { label: 'Refunded',    cls: 'bg-gray-800 text-gray-400 border-gray-700' };
        case 'waived':    return { label: 'Waived',      cls: 'bg-gray-800 text-gray-400 border-gray-700' };
        case 'no_rate':   return { label: 'No rate set', cls: 'bg-gray-800 text-gray-500 border-gray-700' };
        default:          return { label: s,             cls: 'bg-gray-800 text-gray-400 border-gray-700' };
      }
    }

    // Status multiselect dropdown
    const [statusFilters, setStatusFilters] = React.useState<PayStatus[]>([]);
    const [statusOpen, setStatusOpen] = React.useState(false);
    const statusRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      function onOutside(e: MouseEvent) {
        if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusOpen(false);
      }
      document.addEventListener('mousedown', onOutside);
      return () => document.removeEventListener('mousedown', onOutside);
    }, []);

    const [dateFrom, setDateFrom] = React.useState('');
    const [dateTo, setDateTo] = React.useState('');
    const [groupByMonth, setGroupByMonth] = React.useState(false);

    // Index invoices by appointment_id
    const invoiceByAppt = React.useMemo(() => {
      const map: Record<string, typeof clientInvoices[0]> = {};
      for (const inv of clientInvoices) {
        if (inv.appointment_id) map[inv.appointment_id] = inv;
      }
      return map;
    }, []);

    // Billable sessions, filtered
    const allBillable = React.useMemo(() =>
      (appointments as any[])
        .filter(a => (!a.appointment_type || a.appointment_type === 'billable') && paymentStatuses[a.id]?.pricing_rate_cents != null)
        .sort((a: any, b: any) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()),
    []);

    const filtered = React.useMemo(() => {
      return allBillable.filter((a: any) => {
        if (dateFrom && new Date(a.scheduled_at) < new Date(dateFrom)) return false;
        if (dateTo   && new Date(a.scheduled_at) > new Date(dateTo + 'T23:59:59')) return false;
        if (statusFilters.length > 0) {
          const ps = paymentStatuses[a.id];
          const s = (ps?.status === 'manual' ? 'succeeded' : ps?.status) ?? 'no_rate';
          if (!statusFilters.includes(s as PayStatus)) return false;
        }
        return true;
      });
    }, [allBillable, dateFrom, dateTo, statusFilters]);

    const summary = React.useMemo(() => ({
      total: filtered.length,
      paid_cents: filtered.reduce((sum: number, a: any) => {
        const ps = paymentStatuses[a.id];
        if (ps?.status === 'succeeded' || ps?.status === 'manual') return sum + (ps.payment?.amount_cents ?? 0);
        return sum;
      }, 0),
      outstanding_cents: filtered.reduce((sum: number, a: any) => {
        const ps = paymentStatuses[a.id];
        if (!ps || ['unpaid', 'pending', 'failed', 'no_rate'].includes(ps.status)) {
          return sum + (ps?.pricing_rate_cents ?? 0);
        }
        return sum;
      }, 0),
    }), [filtered]);

    function formatDate(iso: string) {
      return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }
    function formatDateTime(iso: string) {
      return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    }

    const isAll = statusFilters.length === 0 || statusFilters.length === STATUS_OPTIONS.length;
    const statusLabel = isAll ? 'All statuses' : statusFilters.map(v => STATUS_OPTIONS.find(o => o.value === v)?.label ?? v).join(', ');

    const renderRow = (a: any) => {
      const ps = paymentStatuses[a.id];
      const statusKey = ps?.status ?? 'no_rate';
      const { label, cls } = statusBadge(statusKey);
      const isOutstanding = !ps || ['unpaid', 'pending', 'failed', 'no_rate'].includes(ps.status);
      const rateCents = ps?.pricing_rate_cents;
      const invoice = invoiceByAppt[a.id];
      const canPay = invoice && invoice.status !== 'paid' && invoice.status !== 'void';
      return (
        <tr key={a.id} className={`transition-colors ${isOutstanding ? 'bg-red-950/10' : 'hover:bg-dark-hover'}`}>
          <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap text-xs">
            {a.scheduled_at ? formatDate(a.scheduled_at) : '—'}
          </td>
          <td className="px-4 py-3 text-center text-gray-300 max-w-[200px] truncate text-xs" title={a.meeting_name}>
            {a.meeting_name || 'Session'}
          </td>
          <td className="px-4 py-3 text-center text-gray-400 text-xs">
            {rateCents != null ? formatCents(rateCents) : <span className="text-gray-600 italic">—</span>}
          </td>
          <td className="px-4 py-3 text-center">
            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${cls}`}>
              {label}
            </span>
          </td>
          <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap capitalize text-xs">
            {statusKey === 'succeeded' ? 'Stripe' : statusKey === 'manual' ? (ps?.payment?.payment_method || 'Manual') : <span className="text-gray-600">—</span>}
          </td>
          <td className="px-4 py-3 text-center text-gray-400 whitespace-nowrap text-xs">
            {ps?.payment?.paid_at ? formatDateTime(ps.payment.paid_at) : '—'}
          </td>
          <td className="px-4 py-3 text-center text-xs">
            {invoice ? (
              <a href={invoice.payment_url} target="_blank" rel="noopener noreferrer"
                className={`font-medium ${canPay ? 'text-primary-400 hover:text-primary-300' : 'text-gray-500 hover:text-gray-400'}`}>
                {canPay ? 'Pay →' : 'View'}
              </a>
            ) : <span className="text-gray-600">—</span>}
          </td>
        </tr>
      );
    };

    return (
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-white">Payments</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your session payment history</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Sessions</p>
            <p className="text-2xl font-bold text-white">{summary.total}</p>
          </div>
          <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Paid</p>
            <p className="text-2xl font-bold text-green-400">{formatCents(summary.paid_cents)}</p>
          </div>
          <div className="bg-dark-sidebar border border-dark-border rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Outstanding</p>
            <p className="text-2xl font-bold text-red-400">{formatCents(summary.outstanding_cents)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]" />
          </div>
          {/* Status multiselect */}
          <div ref={statusRef} className="relative">
            <button type="button" onClick={() => setStatusOpen(p => !p)}
              className={`flex items-center gap-1.5 bg-dark-sidebar border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 whitespace-nowrap max-w-[200px] ${isAll ? 'border-dark-border' : 'border-primary-500/60'}`}>
              <span className="truncate">{statusLabel}</span>
              <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${statusOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {statusOpen && (
              <div className="absolute top-full mt-1 left-0 z-30 bg-dark-card border border-dark-border rounded-lg shadow-xl min-w-[160px] py-1">
                <button type="button" onClick={() => setStatusFilters([])}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 hover:bg-dark-hover transition-colors">
                  <span className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center ${isAll ? 'bg-primary-600 border-primary-500' : 'border-gray-600'}`}>
                    {isAll && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </span>
                  All statuses
                </button>
                <div className="h-px bg-dark-border mx-2 my-1" />
                {STATUS_OPTIONS.map(opt => {
                  const checked = statusFilters.includes(opt.value);
                  return (
                    <button key={opt.value} type="button"
                      onClick={() => setStatusFilters(prev => checked ? prev.filter(v => v !== opt.value) : [...prev, opt.value])}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-300 hover:bg-dark-hover transition-colors">
                      <span className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center ${checked ? 'bg-primary-600 border-primary-500' : 'border-gray-600'}`}>
                        {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </span>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none ml-auto">
            <input type="checkbox" checked={groupByMonth} onChange={e => setGroupByMonth(e.target.checked)} className="accent-primary-500" />
            Group by month
          </label>
          {(dateFrom || dateTo || statusFilters.length > 0) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setStatusFilters([]); }}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="text-sm text-gray-500">No sessions found for the selected filters.</div>
        ) : (
          <div className="bg-dark-sidebar border border-dark-border rounded-xl overflow-x-auto">
            <table className="min-w-max w-full text-xs">
              <thead>
                <tr className="border-b border-dark-border text-gray-500 uppercase tracking-widest text-[10px]">
                  <th className="px-4 py-3 text-center font-medium">Session Date</th>
                  <th className="px-4 py-3 text-center font-medium">Session</th>
                  <th className="px-4 py-3 text-center font-medium">Session Fee</th>
                  <th className="px-4 py-3 text-center font-medium">Status</th>
                  <th className="px-4 py-3 text-center font-medium">Payment Method</th>
                  <th className="px-4 py-3 text-center font-medium">Payment Date</th>
                  <th className="px-4 py-3 text-center font-medium">Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {(() => {
                  if (!groupByMonth) return filtered.map(renderRow);
                  const groups: { key: string; label: string; rows: any[] }[] = [];
                  const seen = new Map<string, number>();
                  filtered.forEach((a: any) => {
                    const d = a.scheduled_at ? new Date(a.scheduled_at) : null;
                    const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'unknown';
                    const mLabel = d ? d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : 'Unknown';
                    if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ key, label: mLabel, rows: [] }); }
                    groups[seen.get(key)!].rows.push(a);
                  });
                  return groups.flatMap(({ label: mLabel, rows }) => [
                    <tr key={`g-${mLabel}`} className="bg-dark-hover/60 border-t border-dark-border">
                      <td colSpan={7} className="px-4 py-2 text-xs font-semibold text-white">
                        {mLabel}
                        <span className="ml-3 font-normal text-gray-500">{rows.length} session{rows.length !== 1 ? 's' : ''}</span>
                      </td>
                    </tr>,
                    ...rows.map(renderRow),
                  ]);
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const ClientFormsSection = () => {
    const [pendingForms, setPendingForms] = React.useState<any[]>([]);
    const [pastResponses, setPastResponses] = React.useState<QuestionnaireResponsesData[]>([]);
    const [loadingForms, setLoadingForms] = React.useState(true);
    const [loadingHistory, setLoadingHistory] = React.useState(false);
    const [expandedPast, setExpandedPast] = React.useState<string | null>(null);

    React.useEffect(() => {
      const token = localStorage.getItem('authToken') || '';
      const authHdr = { Authorization: `Bearer ${token}` };
      Promise.all([
        fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.pending(clientId)}`, { headers: authHdr })
          .then(r => r.ok ? r.json() : { questionnaires: [] }).catch(() => ({ questionnaires: [] })),
        fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.completedByClient(clientId)}`, { headers: authHdr })
          .then(r => r.ok ? r.json() : { questionnaires: [] }).catch(() => ({ questionnaires: [] })),
      ]).then(async ([pending, completed]) => {
        const pForms = (pending.questionnaires || []).filter((q: any) => q.source === 'form');
        const cForms = (completed.questionnaires || []).filter((q: any) => q.source === 'form');
        setPendingForms(pForms);
        if (cForms.length > 0) {
          setLoadingHistory(true);
          const results = await Promise.all(
            cForms.map(async (q: any) => {
              try {
                const res = await fetch(
                  `${config.apiBaseUrl}${endpoints.main.questionnaires.myResponses(q.id)}`,
                  { headers: authHdr }
                );
                return res.ok ? await res.json() as QuestionnaireResponsesData : null;
              } catch { return null; }
            })
          );
          setPastResponses(results.filter((r): r is QuestionnaireResponsesData => r !== null));
          setLoadingHistory(false);
        }
      }).finally(() => setLoadingForms(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isEmpty = !loadingForms && pendingForms.length === 0 && pastResponses.length === 0;

    return (
      <div className="p-8 max-w-3xl space-y-6">
        <h1 className="text-2xl font-bold text-gray-100">Forms</h1>

        {loadingForms ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-slate-800 rounded-2xl border border-slate-700 p-5 animate-pulse">
                <div className="h-4 bg-slate-700 rounded w-48 mb-2" />
                <div className="h-3 bg-slate-700/50 rounded w-32" />
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-12 text-center">
            <DocumentTextIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No forms assigned yet.</p>
            <p className="text-slate-500 text-sm mt-1">Your coach will assign forms for you to complete here.</p>
          </div>
        ) : (
          <>
            {/* Pending forms */}
            {pendingForms.length > 0 && (
              <div className="space-y-3">
                {pendingForms.map((form: any) => {
                  const isDue = form.next_due_date && new Date(form.next_due_date) <= new Date();
                  return (
                    <div key={form.id} className="bg-slate-800 rounded-2xl border border-slate-700 p-5 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-200 truncate">{form.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {form.next_due_date && `Due ${new Date(form.next_due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {isDue ? (
                          <span className="text-xs px-2.5 py-1 rounded-full bg-red-900/40 text-red-300 font-medium">Overdue</span>
                        ) : (
                          <span className="text-xs px-2.5 py-1 rounded-full bg-amber-900/30 text-amber-300 font-medium">Pending</span>
                        )}
                        {form.access_token && (
                          <a
                            href={`/checkin/${form.access_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            Fill Out →
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Past Responses */}
            {!loadingHistory && pastResponses.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-gray-200 mb-3">Past Responses</h2>
                <div className="space-y-3">
                  {pastResponses.map(item => {
                    const dateSet = new Set<string>();
                    item.questions.forEach(q => q.data_points.forEach(dp => dateSet.add(dp.submitted_at)));
                    const dates = Array.from(dateSet).sort().reverse();
                    const isExpanded = expandedPast === item.questionnaire.id;
                    return (
                      <div key={item.questionnaire.id} className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpandedPast(isExpanded ? null : item.questionnaire.id)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-700/50 transition-colors"
                        >
                          <div>
                            <p className="font-semibold text-gray-200">{item.questionnaire.title}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {dates.length} response{dates.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <ChevronDownIcon
                            className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ml-3 ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {isExpanded && (
                          <div className="border-t border-slate-700 divide-y divide-slate-700">
                            {dates.map(submittedAt => {
                              const questionsForDate = item.questions.map(q => ({
                                ...q,
                                data_points: q.data_points.filter(dp => dp.submitted_at === submittedAt),
                              })).filter(q => q.data_points.length > 0);
                              return (
                                <div key={submittedAt} className="px-5 py-3 flex items-center justify-between gap-4">
                                  <div>
                                    <p className="text-sm text-gray-200">
                                      Submitted {new Date(submittedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </p>
                                    {item.questionnaire.next_due_date && (
                                      <p className="text-xs text-slate-400 mt-0.5">
                                        Due {new Date(item.questionnaire.next_due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setResponseViewer({ title: item.questionnaire.title, questions: questionsForDate })}
                                    className="text-xs text-blue-400 hover:text-blue-300 underline flex-shrink-0"
                                  >
                                    View Responses
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const handleClientFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setClientUploadFile(f);
    if (f && !clientUploadName) setClientUploadName(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleClientUpload = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!clientUploadFile || !clientUploadName.trim()) return;
    setClientUploading(true);
    setClientUploadError('');
    try {
      const doc = await clientUploadDocument(clientUploadName.trim(), clientUploadFile, clientUploadFolderId || undefined);
      setClientFiles((prev) => [...prev, doc]);
      setClientUploadName('');
      setClientUploadFile(null);
      setClientUploadFolderId('');
      if (clientFileInputRef.current) clientFileInputRef.current.value = '';
    } catch (e: any) {
      setClientUploadError(e.message || 'Upload failed');
    } finally {
      setClientUploading(false);
    }
  };

  const handleClientCreateFolder = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!newClientFolderName.trim()) return;
    setCreatingClientFolder(true);
    try {
      const folder = await createClientFolderSvc(newClientFolderName.trim());
      setClientFolders((prev) => [...prev, folder]);
      setNewClientFolderName('');
      setShowClientFolderInput(false);
    } catch (e: any) {
      alert(e.message || 'Failed to create folder');
    } finally {
      setCreatingClientFolder(false);
    }
  };

  const handleClientView = async (docId: string) => {
    setDocViewLoading(docId);
    try {
      const res = await getDocumentViewUrl(docId);
      setDocViewer(res);
    } catch (e: any) {
      alert(e.message || 'Could not load document');
    } finally {
      setDocViewLoading(null);
    }
  };

  const handleClientDeleteFile = async (id: string) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      await deleteClientFile(id);
      setClientFiles((prev) => prev.filter((f) => f.id !== id));
    } catch (e: any) {
      alert(e.message || 'Delete failed');
    }
  };

  const handleMoveFile = async (fileId: string, folderId: string | null) => {
    try {
      await moveDocument(fileId, folderId);
      setClientFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, folder_id: folderId } : f));
    } catch (e: any) {
      alert(e.message || 'Move failed');
    }
  };

  const handleMovePacket = async (packetId: string, folderId: string | null) => {
    try {
      await movePacketToFolder(packetId, folderId);
      setPackets((prev) => prev.map((p) => p.id === packetId ? { ...p, folder_id: folderId } : p));
    } catch (e: any) {
      alert(e.message || 'Move failed');
    }
  };


  const handleViewPacket = async (packet: DocumentPacketSummary) => {
    setPacketViewLoading(packet.id);
    try {
      const full = await getPacketByToken(packet.sign_token);
      const docs = full.items.map((item) => ({
        url: item.view_url || documentViewUrl(packet.sign_token, item.document_id),
        fileType: item.document?.file_type ?? 'pdf',
        docName: item.document?.name ?? 'Document',
      }));
      setPacketViewer({ title: packet.title, docs });
    } catch (e: any) {
      alert(e.message || 'Could not load documents');
    } finally {
      setPacketViewLoading(null);
    }
  };

  const fmtBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const DocumentsSection = () => {
    const coachPackets = coachFilter ? packets.filter(p => p.coach_id === coachFilter) : packets;

    // Items visible in the current folder selection
    const visibleFiles = selectedClientFolderId === 'all'
      ? clientFiles
      : selectedClientFolderId === null
        ? clientFiles.filter((f) => !f.folder_id)
        : clientFiles.filter((f) => f.folder_id === selectedClientFolderId);

    const visiblePackets = selectedClientFolderId === 'all'
      ? coachPackets
      : selectedClientFolderId === null
        ? coachPackets.filter((p) => !p.folder_id)
        : coachPackets.filter((p) => p.folder_id === selectedClientFolderId);

    const totalItems = clientFiles.length + coachPackets.length;

    return (
    <div className="flex flex-col h-full p-6 gap-3">
      <CoachFilterChips />
      <div className="flex gap-4 flex-1 min-h-0">
      {/* ── Folder sidebar ── */}
      <div className="w-48 flex-shrink-0">
        <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setSelectedClientFolderId('all')}
            className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-slate-700/50 ${selectedClientFolderId === 'all' ? 'bg-blue-900/30 text-blue-300 font-medium' : 'text-slate-300'}`}
          >
            <FolderOpenIcon className="w-4 h-4 flex-shrink-0" />
            All Documents
            <span className="ml-auto text-xs text-slate-500">{totalItems}</span>
          </button>
          <button
            onClick={() => setSelectedClientFolderId(null)}
            className={`w-full flex items-center gap-2 pl-7 pr-3 py-2 text-sm text-left hover:bg-slate-700/50 border-t border-slate-700 ${selectedClientFolderId === null ? 'bg-blue-900/30 text-blue-300 font-medium' : 'text-slate-400'}`}
          >
            <DocumentIcon className="w-3.5 h-3.5 flex-shrink-0" />
            Unfiled
            <span className="ml-auto text-xs text-slate-500">
              {clientFiles.filter((f) => !f.folder_id).length + packets.filter((p) => !p.folder_id).length}
            </span>
          </button>
          {clientFolders.map((f) => (
            <button
              key={f.id}
              onClick={() => setSelectedClientFolderId(f.id)}
              className={`w-full flex items-center gap-2 pl-7 pr-3 py-2 text-sm text-left hover:bg-slate-700/50 border-t border-slate-700 ${selectedClientFolderId === f.id ? 'bg-blue-900/30 text-blue-300 font-medium' : 'text-slate-400'}`}
            >
              <FolderIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{f.name}</span>
              <span className="ml-auto text-xs text-slate-500 flex-shrink-0">
                {clientFiles.filter((fi) => fi.folder_id === f.id).length + packets.filter((p) => p.folder_id === f.id).length}
              </span>
            </button>
          ))}
        </div>
        {showClientFolderInput ? (
          <form onSubmit={handleClientCreateFolder} className="mt-2 flex gap-1">
            <input
              autoFocus
              value={newClientFolderName}
              onChange={(e) => setNewClientFolderName(e.target.value)}
              placeholder="Folder name"
              className="flex-1 min-w-0 px-2 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-xs text-gray-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button type="submit" disabled={creatingClientFolder} className="px-2 py-1.5 bg-blue-600 text-white text-xs rounded-lg disabled:opacity-50">
              {creatingClientFolder ? '…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setShowClientFolderInput(false); setNewClientFolderName(''); }} className="px-2 py-1.5 text-slate-400 hover:text-slate-200 text-xs">✕</button>
          </form>
        ) : (
          <button onClick={() => setShowClientFolderInput(true)} className="mt-2 w-full flex items-center gap-1.5 px-3 py-2 text-xs text-slate-500 hover:text-blue-400 hover:bg-slate-800 rounded-lg border border-dashed border-slate-600">
            <PlusIcon className="w-3.5 h-3.5" /> New Folder
          </button>
        )}
      </div>

      {/* ── Main content panel ── */}
      <div className="flex-1 min-w-0 space-y-4">

        {pendingPackets.length > 0 && (
          <div className="flex items-center gap-3 p-4 bg-amber-900/20 border border-amber-700/30 rounded-2xl text-amber-300 text-sm">
            <ExclamationCircleIcon className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <span>
              You have <strong>{pendingPackets.length}</strong> document packet
              {pendingPackets.length > 1 ? 's' : ''} waiting for your signature.
            </span>
          </div>
        )}

        {/* ── Upload form ── */}
        <form onSubmit={handleClientUpload} className="p-4 bg-slate-800/50 border border-dashed border-slate-600 rounded-xl space-y-3">
          <p className="text-sm font-medium text-slate-300">Upload a file</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Display name"
              value={clientUploadName}
              onChange={(e) => setClientUploadName(e.target.value)}
              className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-gray-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <label className="flex items-center gap-2 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-300 cursor-pointer hover:bg-slate-600 flex-shrink-0">
              <ArrowUpTrayIcon className="w-4 h-4 text-slate-400" />
              {clientUploadFile ? clientUploadFile.name : 'Choose file'}
              <input
                ref={clientFileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={handleClientFileChange}
              />
            </label>
            <button
              type="submit"
              disabled={clientUploading || !clientUploadFile}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex-shrink-0"
            >
              {clientUploading ? '…' : 'Upload'}
            </button>
          </div>
          {clientFolders.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Save to folder</label>
              <select
                value={clientUploadFolderId}
                onChange={(e) => setClientUploadFolderId(e.target.value)}
                className="block w-44 rounded-lg border border-slate-600 bg-slate-800 text-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Unfiled</option>
                {clientFolders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          )}
          {clientUploadError && <p className="text-red-400 text-sm">{clientUploadError}</p>}
        </form>

        {/* ── Unified file + packet list ── */}
        {visibleFiles.length === 0 && visiblePackets.length === 0 ? (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-10 text-center">
            <DocumentIcon className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            <p className="text-sm text-slate-400">
              {totalItems === 0 ? 'No documents yet.' : 'No documents in this folder.'}
            </p>
          </div>
        ) : (
          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
            <div className="divide-y divide-slate-700">

              {/* My files */}
              {visibleFiles.map((f) => (
                <div key={`file-${f.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-slate-700/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <DocumentIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-200 truncate">{f.name}</p>
                      <p className="text-xs text-slate-500">
                        My file · {fmtBytes(f.file_size)}
                        {f.created_at && ` · ${new Date(f.created_at).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                    {clientFolders.length > 0 && (
                      <select
                        value={f.folder_id ?? ''}
                        onChange={(e) => handleMoveFile(f.id, e.target.value || null)}
                        className="text-xs border border-slate-600 rounded px-1.5 py-1 bg-slate-700 text-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-500 mr-1"
                        title="Move to folder"
                      >
                        <option value="">Unfiled</option>
                        {clientFolders.map((fo) => <option key={fo.id} value={fo.id}>{fo.name}</option>)}
                      </select>
                    )}
                    <button
                      onClick={() => handleClientView(f.id)}
                      disabled={docViewLoading === f.id}
                      className="p-1.5 text-slate-500 hover:text-blue-400 rounded disabled:opacity-50"
                      title="View"
                    >
                      {docViewLoading === f.id
                        ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400" />
                        : <EyeIcon className="w-4 h-4" />
                      }
                    </button>
                    <button
                      onClick={() => handleClientDeleteFile(f.id)}
                      className="p-1.5 text-slate-500 hover:text-red-400 rounded"
                      title="Delete"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Packets from coach */}
              {visiblePackets.map((p) => (
                <div key={`pkt-${p.id}`} className="flex items-center justify-between px-5 py-4 hover:bg-slate-700/50">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-200 text-sm truncate">{p.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      From coach · Sent {p.sent_at ? new Date(p.sent_at).toLocaleDateString() : '—'}
                      {p.signed_at && ` · Signed ${new Date(p.signed_at).toLocaleDateString()}`}
                      {p.signed_ip_address && ` · IP ${p.signed_ip_address}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                    {clientFolders.length > 0 && (
                      <select
                        value={p.folder_id ?? ''}
                        onChange={(e) => handleMovePacket(p.id, e.target.value || null)}
                        className="text-xs border border-slate-600 rounded px-1.5 py-1 bg-slate-700 text-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        title="Move to folder"
                      >
                        <option value="">Unfiled</option>
                        {clientFolders.map((fo) => <option key={fo.id} value={fo.id}>{fo.name}</option>)}
                      </select>
                    )}
                    {p.status === 'signed' || p.status === 'signed_offline' ? (
                      <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-green-900/40 text-green-300 font-medium">
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                        Signed
                      </span>
                    ) : (
                      <>
                        <span className="text-xs px-2.5 py-1 rounded-full bg-amber-900/30 text-amber-300 font-medium">
                          Pending
                        </span>
                        <button
                          onClick={() => navigate(`/sign/${p.sign_token}`)}
                          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Review &amp; Sign
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleViewPacket(p)}
                      disabled={packetViewLoading === p.id}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-blue-400 border border-slate-600 hover:border-blue-500 rounded-lg disabled:opacity-40 transition-colors"
                      title="View documents"
                    >
                      {packetViewLoading === p.id
                        ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-400" />
                        : <EyeIcon className="w-3.5 h-3.5" />}
                      View
                    </button>
                  </div>
                </div>
              ))}

            </div>
          </div>
        )}

      </div>
      </div>
    </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const coachBrandColor = dashboard?.coach?.brand_color || null;
  const clientSidebarText = coachBrandColor ? getContrastColor(coachBrandColor) : undefined;

  return (
    <div className="pm-client-branded flex bg-slate-900 overflow-hidden" style={{ height: 'calc(100vh - 72px)' }}>

      {/* Dynamic brand color overrides */}
      {coachBrandColor && /^#[0-9a-f]{6}$/i.test(coachBrandColor) && (
        <style>{`
          .pm-client-branded .bg-primary-600 { background-color: ${coachBrandColor} !important; }
          .pm-client-branded .hover\\:bg-primary-700:hover { background-color: ${darkenHex(coachBrandColor, 15)} !important; }
          .pm-client-branded .text-primary-300 { color: ${lightenHex(coachBrandColor, 40)} !important; }
          .pm-client-branded .border-primary-500 { border-color: ${coachBrandColor} !important; }
          .pm-client-branded .focus\\:ring-primary-500:focus { --tw-ring-color: ${coachBrandColor} !important; }
        `}</style>
      )}

      {/* Sidebar */}
      <aside
        className={`w-56 flex-shrink-0 ${coachBrandColor ? '' : 'bg-slate-950'} border-r flex flex-col`}
        style={coachBrandColor ? { backgroundColor: coachBrandColor, borderColor: `${coachBrandColor}40` } : { borderColor: '#1e293b' }}
      >
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.slice(0, navItems.findIndex(n => n.key === 'documents')).map(({ key, label, Icon, badge }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                !coachBrandColor
                  ? activeSection === key ? 'bg-blue-700/40 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  : ''
              }`}
              style={coachBrandColor ? {
                color: clientSidebarText,
                backgroundColor: activeSection === key ? `${clientSidebarText}22` : undefined,
                opacity: activeSection === key ? 1 : 0.75,
              } : undefined}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              {badge !== undefined && <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{badge}</span>}
            </button>
          ))}

          {/* Financials accordion */}
          <button
            onClick={() => setFinancialsOpen(p => !p)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              !coachBrandColor
                ? ['payments', 'invoices'].includes(activeSection) ? 'bg-blue-700/40 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                : ''
            }`}
            style={coachBrandColor ? {
              color: clientSidebarText,
              backgroundColor: ['payments', 'invoices'].includes(activeSection) ? `${clientSidebarText}22` : undefined,
              opacity: ['payments', 'invoices'].includes(activeSection) ? 1 : 0.75,
            } : undefined}
          >
            <BanknotesIcon className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1 text-left">Financials</span>
            <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${financialsOpen ? 'rotate-180' : ''}`} />
          </button>
          {financialsOpen && (
            <div className="ml-4 space-y-0.5">
              {([
                { key: 'payments' as Section, label: 'Payments' },
                { key: 'invoices' as Section, label: 'Invoices' },
              ]).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !coachBrandColor
                      ? activeSection === key ? 'bg-blue-700/40 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                      : ''
                  }`}
                  style={coachBrandColor ? {
                    color: clientSidebarText,
                    backgroundColor: activeSection === key ? `${clientSidebarText}22` : undefined,
                    opacity: activeSection === key ? 1 : 0.75,
                  } : undefined}
                >
                  <span className="flex-1 text-left">{label}</span>
                </button>
              ))}
            </div>
          )}

          {navItems.slice(navItems.findIndex(n => n.key === 'documents')).map(({ key, label, Icon, badge }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                !coachBrandColor
                  ? activeSection === key ? 'bg-blue-700/40 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  : ''
              }`}
              style={coachBrandColor ? {
                color: clientSidebarText,
                backgroundColor: activeSection === key ? `${clientSidebarText}22` : undefined,
                opacity: activeSection === key ? 1 : 0.75,
              } : undefined}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              {badge !== undefined && <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{badge}</span>}
            </button>
          ))}
        </nav>

        {/* Bottom: profile avatar + dropdown */}
        <div className="px-3 py-4 border-t space-y-1" style={{ borderColor: clientSidebarText ? `${clientSidebarText}30` : '#1e293b' }}>
          {dashboard?.coach && (
            <div className="px-3 py-2 flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-900/50" style={coachBrandColor ? { backgroundColor: `${clientSidebarText}20` } : undefined}>
                <span className="text-xs font-semibold text-blue-300" style={coachBrandColor ? { color: clientSidebarText } : undefined}>
                  {dashboard.coach.first_name[0]}{dashboard.coach.last_name[0]}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-xs truncate text-slate-500" style={coachBrandColor ? { color: `${clientSidebarText}80` } : undefined}>Your coach</p>
                <p className="text-xs font-medium truncate text-slate-300" style={coachBrandColor ? { color: clientSidebarText } : undefined}>
                  {dashboard.coach.first_name} {dashboard.coach.last_name}
                </p>
              </div>
            </div>
          )}
          <button
            onClick={() => navigate('/help')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${!coachBrandColor ? 'text-slate-500 hover:bg-slate-800 hover:text-slate-300' : ''}`}
            style={coachBrandColor ? { color: clientSidebarText, opacity: 0.75 } : undefined}
          >
            <QuestionMarkCircleIcon className="w-4 h-4 flex-shrink-0" />
            Help
          </button>
          <button
            onClick={() => navigate('/support')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${!coachBrandColor ? 'text-slate-500 hover:bg-slate-800 hover:text-slate-300' : ''}`}
            style={coachBrandColor ? { color: clientSidebarText, opacity: 0.75 } : undefined}
          >
            <QuestionMarkCircleIcon className="w-4 h-4 flex-shrink-0" />
            Support
          </button>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${!coachBrandColor ? 'text-slate-500 hover:bg-slate-800 hover:text-slate-300' : ''}`}
            style={coachBrandColor ? { color: clientSidebarText, opacity: 0.75 } : undefined}
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4 flex-shrink-0" />
            Logout
          </button>
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => { setProfileMenuOpen(v => !v); setProfileMenuTab('menu'); }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${!coachBrandColor ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200' : ''}`}
              style={coachBrandColor ? { color: clientSidebarText, opacity: 0.75 } : undefined}
            >
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {clientInitials}
              </div>
              <span className="flex-1 text-left text-xs truncate">{firstName}</span>
              <ChevronDownIcon className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
            </button>
            {profileMenuOpen && (
              <div className="absolute bottom-full left-0 mb-2 w-72 bg-slate-800 border border-slate-700 rounded-xl shadow-xl shadow-black/50 z-50 overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b border-slate-700 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {clientInitials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-200 truncate">{firstName} {clientLastName}</p>
                    {clientEmail && <p className="text-xs text-slate-400 truncate">{clientEmail}</p>}
                  </div>
                  <button onClick={() => { setProfileMenuOpen(false); setProfileMenuTab('menu'); }} className="ml-auto text-slate-500 hover:text-slate-300 flex-shrink-0">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Change Password inline form */}
                {profileMenuTab === 'password' ? (
                  <div className="p-5 space-y-4">
                    <button onClick={() => setProfileMenuTab('menu')} className="flex items-center gap-1 text-xs text-slate-400 hover:text-gray-200 transition-colors mb-1">
                      <ChevronRightIcon className="w-3 h-3 rotate-180" /> Back
                    </button>
                    <p className="text-sm font-semibold text-gray-200">Change Password</p>
                    <ClientChangePasswordForm changePassword={changePassword} />
                  </div>
                ) : (
                  /* Action menu */
                  <div className="py-2">
                    <button
                      onClick={() => { setProfileMenuOpen(false); setSettingsSubSection('profile'); setActiveSection('settings'); }}
                      className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                    >
                      <UserCircleIcon className="w-4 h-4 text-slate-500" />
                      Edit Profile
                    </button>
                    <button
                      onClick={() => setProfileMenuTab('password')}
                      className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                    >
                      <ShieldCheckIcon className="w-4 h-4 text-slate-500" />
                      Change Password
                    </button>
                    <button
                      onClick={() => { setProfileMenuOpen(false); setFinancialsOpen(true); setActiveSection('invoices'); }}
                      className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                    >
                      <CreditCardIcon className="w-4 h-4 text-slate-500" />
                      Payment History
                    </button>
                    <button
                      onClick={() => { setProfileMenuOpen(false); setSettingsSubSection(null); setActiveSection('settings'); }}
                      className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                    >
                      <Cog6ToothIcon className="w-4 h-4 text-slate-500" />
                      Settings
                    </button>
                  </div>
                )}

                {/* Footer: Sign out */}
                <div className="border-t border-slate-700 px-5 py-3">
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 transition-colors">
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">

        {/* Non-blocking prompt for grandfathered clients to complete DOB/state/country (E1.6) */}
        <ProfileCompletionBanner />

        {/* Scrollable section content */}
        <div className="flex-1 overflow-y-auto">
          {activeSection === 'home'         && <HomeSection />}
          {activeSection === 'sessions'     && <SessionsSection />}
          {activeSection === 'action-items' && <ActionItemsSection />}
          {activeSection === 'forms'        && <ClientFormsSection />}
          {activeSection === 'check-ins'    && <CheckInsSection />}
          {activeSection === 'notes'        && <NotesSection />}
          {activeSection === 'payments'     && <PaymentsSection />}
          {activeSection === 'documents'    && <DocumentsSection />}
          {activeSection === 'invoices'     && <InvoicesSection />}
          {activeSection === 'settings'     && <SettingsSection settingsSubSection={settingsSubSection} setSettingsSubSection={setSettingsSubSection} onTermsAccepted={setTermsAccepted} onPrivacyAccepted={setPrivacyAccepted} />}
        </div>
      </main>

      {/* Session Summary Modal */}
      {selectedAppointment && (
        <SessionSummaryPanel
          appointmentId={selectedAppointment.id}
          meetingName={selectedAppointment.meeting_name}
          role="client"
          onClose={() => setSelectedAppointment(null)}
        />
      )}

      {/* Package Payment Modal */}
      {payingPackagePayment && (
        <PackagePaymentModal
          packagePaymentId={payingPackagePayment.id}
          packageName={payingPackagePayment.package_name}
          label={payingPackagePayment.label}
          onClose={() => setPayingPackagePayment(null)}
          onSuccess={() => {
            setPendingPackagePayments(prev => prev.filter(p => p.id !== payingPackagePayment.id));
            setPayingPackagePayment(null);
          }}
        />
      )}

      {/* Payment Modal */}
      {payingAppointment && (
        <PaymentModal
          appointmentId={payingAppointment.id}
          meetingName={payingAppointment.meeting_name}
          onClose={() => setPayingAppointment(null)}
          onSuccess={() => {
            setPaymentStatuses(prev => ({
              ...prev,
              [payingAppointment.id]: {
                appointment_id: payingAppointment.id,
                status: 'succeeded',
                payment: prev[payingAppointment.id]?.payment ?? null,
              },
            }));
            setPayingAppointment(null);
          }}
        />
      )}
      {showBookModal && dashboard?.coach && (
        <BookSessionModal
          coachId={dashboard.coach.id}
          coachName={`${dashboard.coach.first_name} ${dashboard.coach.last_name}`}
          aiConsent={aiConsent}
          onClose={() => setShowBookModal(false)}
          onBooked={() => { fetchData(); }}
        />
      )}

      {/* Document Viewer (single file) */}
      {docViewer && (
        <DocumentViewerPanel
          url={docViewer.url}
          fileType={docViewer.file_type}
          docName={docViewer.doc_name}
          onClose={() => setDocViewer(null)}
        />
      )}

      {/* Packet Viewer (multi-doc agreement) */}
      {packetViewer && (
        <DocumentViewerPanel
          documents={packetViewer.docs}
          title={packetViewer.title}
          onClose={() => setPacketViewer(null)}
        />
      )}

      {/* Response Viewer Modal (check-ins and forms) */}
      {responseViewer && (
        <ResponseViewerModal
          title={responseViewer.title}
          questions={responseViewer.questions}
          onClose={() => setResponseViewer(null)}
        />
      )}

    </div>
  );
};

export default ClientDashboard;
